import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, Play, Building2, Briefcase, UserCircle,
  Plug, Settings, LogOut, RefreshCw, Trash2, Ban, CheckCircle2,
  XCircle, AlertCircle, Clock, Database, Youtube, Zap, CreditCard,
  Globe, Eye, ExternalLink, Shield, AlertTriangle,
  MessageCircle, Send, MousePointerClick, TrendingUp, X, ChevronDown, Menu
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { API } from "../App";
import { niceAxisMax } from "../lib/chartUtils";

const ADMIN_CODE_KEY = "admin_code";

// ─── helpers ────────────────────────────────────────────────────────────────

function adminHeaders() {
  return {
    "Content-Type": "application/json",
    "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "",
  };
}

async function adminFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { ...adminHeaders(), ...(opts.headers || {}) },
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Erreur inconnue" }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function roleBadge(role) {
  const colors = {
    clipper: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/25",
    agency: "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/25",
    manager: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",
    client: "bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/25",
    admin: "bg-red-500/10 text-red-400 border-red-500/25",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium tracking-wide border ${colors[role] || "bg-[#1C1A17] text-[#F5F4F1]/55 border-warm"}`}>
      {role || "—"}
    </span>
  );
}

// ─── CodeGate ────────────────────────────────────────────────────────────────

function CodeGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/admin/verify`, {
        headers: { "X-Admin-Code": code },
        credentials: "include",
      }).then(async (r) => {
        if (!r.ok) throw new Error("invalid");
      });
      localStorage.setItem(ADMIN_CODE_KEY, code);
      onUnlock();
    } catch {
      setShake(true);
      setTimeout(() => setShake(false), 600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0D0B] text-[#F5F4F1] flex items-center justify-center relative">
      <div className="bg-warm-radial absolute inset-0 pointer-events-none" />
      <div className="w-full max-w-sm p-8 relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
              <Shield className="w-4.5 h-4.5 text-[#FF007F]" />
            </div>
            <span className="text-base font-display font-semibold tracking-tight text-[#F5F4F1]">
              The Clip Deal Track
            </span>
          </div>
          <p className="text-[#F5F4F1]/55 text-sm">Accès administrateur restreint</p>
        </div>

        <form onSubmit={handleSubmit} className={`bg-[#1C1A17]/85 border border-warm rounded-2xl p-6 ${shake ? "animate-shake" : ""}`}>
          <div className="mb-4">
            <label className="block text-[11px] text-[#F5F4F1]/55 mb-2 uppercase tracking-widest font-medium">
              Code d'accès
            </label>
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="••••••••••••"
              className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-4 py-3 text-[#F5F4F1] placeholder-[#F5F4F1]/20 focus:outline-none focus:border-[#F5F4F1]/22 transition-colors text-center tracking-widest text-lg"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading || !code}
            className="w-full bg-[#FF007F] hover:bg-[#E50073] disabled:opacity-40 text-white font-semibold py-3 rounded-lg transition-all duration-200"
          >
            {loading ? "Vérification…" : "Entrer"}
          </button>
          {shake && (
            <p className="text-red-400 text-sm text-center mt-3">Code incorrect</p>
          )}
        </form>

        <p className="text-[#F5F4F1]/30 text-[11px] text-center mt-6">
          Accès réservé — toute tentative est journalisée.
        </p>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.4s ease; }
      `}</style>
    </div>
  );
}

// ─── OverviewTab ─────────────────────────────────────────────────────────────

function fmtNum(n) {
  if (n === undefined || n === null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("fr-FR");
}

// Period config
const PERIODS = [
  { key: "1",   label: "24h",    days: 1   },
  { key: "7",   label: "7j",     days: 7   },
  { key: "30",  label: "30j",    days: 30  },
  { key: "365", label: "1 an",   days: 365 },
];

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewsTimeline, setViewsTimeline] = useState([]);
  const [clicksTimeline, setClicksTimeline] = useState([]);
  const [activeChart, setActiveChart] = useState("views"); // "views" | "clicks"
  const [chartPeriod, setChartPeriod] = useState("30");   // "1" | "7" | "30" | "365"
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Initial load: stats + default timeline (30j)
  useEffect(() => {
    Promise.all([
      adminFetch("/admin/stats"),
      adminFetch("/admin/stats/videos-timeline?days=30").catch(() => ({ timeline: [] })),
      adminFetch("/admin/stats/clicks-timeline?days=30").catch(() => ({ timeline: [] })),
    ]).then(([s, vt, ct]) => {
      setStats(s);
      setViewsTimeline(vt.timeline || []);
      setClicksTimeline(ct.timeline || []);
    }).catch((e) => toast.error(e.message))
    .finally(() => setLoading(false));
  }, []);

  // Re-fetch timelines when period changes
  const fetchTimelines = async (days) => {
    setTimelineLoading(true);
    try {
      const [vt, ct] = await Promise.all([
        adminFetch(`/admin/stats/videos-timeline?days=${days}`).catch(() => ({ timeline: [] })),
        adminFetch(`/admin/stats/clicks-timeline?days=${days}`).catch(() => ({ timeline: [] })),
      ]);
      setViewsTimeline(vt.timeline || []);
      setClicksTimeline(ct.timeline || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handlePeriodChange = (key) => {
    setChartPeriod(key);
    const p = PERIODS.find(p => p.key === key);
    if (p) fetchTimelines(p.days);
  };

  // Format x-axis label based on period
  const xTickFormatter = (val) => {
    if (chartPeriod === "1") return val;           // already "HH:00"
    if (chartPeriod === "365") {
      // "YYYY-MM" → "MMM"
      const months = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Aoû","Sep","Oct","Nov","Déc"];
      const m = parseInt(val.slice(5, 7), 10);
      return months[m - 1] || val;
    }
    // daily: "YYYY-MM-DD" → "DD/MM"
    return val ? val.slice(5).replace("-", "/") : val;
  };

  const xInterval = chartPeriod === "1" ? 3 : chartPeriod === "7" ? 0 : chartPeriod === "30" ? 4 : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
    </div>
  );
  if (!stats) return null;

  const topCards = [
    { label: "Utilisateurs", value: stats.users, icon: Users, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/10", border: "border-[#00E5FF]/20" },
    { label: "Campagnes (total)", value: stats.campaigns, icon: Play, color: "text-[#FF007F]", bg: "bg-[#FF007F]/10", border: "border-[#FF007F]/20" },
    { label: "Campagnes au clic", value: stats.click_campaigns || 0, icon: MousePointerClick, color: "text-[#FFB300]", bg: "bg-[#FFB300]/10", border: "border-[#FFB300]/20" },
    { label: "Membres campagne", value: stats.campaign_members, icon: UserCircle, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/10", border: "border-[#00E5FF]/20" },
    { label: "Revenus total", value: `${(stats.total_earnings_eur || 0).toFixed(2)} €`, icon: CreditCard, color: "text-[#39FF14]", bg: "bg-[#39FF14]/10", border: "border-[#39FF14]/20" },
    { label: "Messages", value: stats.messages, icon: MessageCircle, color: "text-[#FF007F]", bg: "bg-[#FF007F]/10", border: "border-[#FF007F]/20" },
  ];

  const viewsCards = [
    { label: "Vues totales", value: fmtNum(stats.total_views || 0), icon: Eye, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/8", border: "border-[#00E5FF]/15" },
    { label: "Vidéos trackées", value: stats.tracked_videos, icon: TrendingUp, color: "text-[#F5F4F1]", bg: "bg-[#0E0D0B]", border: "border-warm" },
    { label: "Comptes sociaux", value: stats.social_accounts, icon: Globe, color: "text-[#FFB300]", bg: "bg-[#FFB300]/8", border: "border-[#FFB300]/15" },
  ];

  const clickCards = [
    { label: "Clics totaux", value: fmtNum(stats.total_clicks || 0), icon: MousePointerClick, color: "text-[#FFB300]", bg: "bg-[#FFB300]/8", border: "border-[#FFB300]/15" },
    { label: "Clics uniques", value: fmtNum(stats.total_unique_clicks || 0), icon: Shield, color: "text-[#39FF14]", bg: "bg-[#39FF14]/8", border: "border-[#39FF14]/15" },
    { label: "Revenus au clic", value: `${(stats.click_earnings_eur || 0).toFixed(2)} €`, icon: CreditCard, color: "text-[#FF007F]", bg: "bg-[#FF007F]/8", border: "border-[#FF007F]/15" },
  ];

  const hasViews = viewsTimeline.some(d => d.views > 0);
  const hasClicks = clicksTimeline.some(d => d.clicks > 0);
  const chartData = activeChart === "views" ? viewsTimeline : clicksTimeline;
  const chartHasData = activeChart === "views" ? hasViews : hasClicks;

  return (
    <div>
      {/* Header */}
      <div className="mb-7">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
            <LayoutDashboard className="w-4.5 h-4.5 text-[#FF007F]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Vue d'ensemble</h2>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">État global de la plateforme — chiffres réels en base.</p>
      </div>

      {/* Top row — general stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {topCards.map((card) => (
          <div key={card.label} className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 hover:border-[#F5F4F1]/14 hover:-translate-y-0.5 transition-all duration-200" title={card.label}>
            <div className={`w-7 h-7 rounded-lg ${card.bg} border ${card.border} flex items-center justify-center mb-3`}>
              <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
            </div>
            <div className="text-xl font-display font-semibold tracking-tight text-[#F5F4F1] tabular-nums">{typeof card.value === "number" ? card.value.toLocaleString("fr-FR") : card.value}</div>
            <div className="text-[#F5F4F1]/55 text-[11px] mt-1 truncate">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Second row — views vs clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        {/* Views section */}
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center">
              <Eye className="w-3.5 h-3.5 text-[#00E5FF]" />
            </div>
            <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">Statistiques vues</p>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {viewsCards.map(c => (
              <div key={c.label} className={`${c.bg} border ${c.border} rounded-lg p-3`}>
                <c.icon className={`w-3.5 h-3.5 ${c.color} mb-2`} />
                <div className="text-base md:text-lg font-display font-semibold tabular-nums text-[#F5F4F1] tracking-tight">{typeof c.value === "number" ? c.value.toLocaleString("fr-FR") : c.value}</div>
                <div className="text-[#F5F4F1]/55 text-[10px] mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Clicks section */}
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="w-7 h-7 rounded-lg bg-[#FFB300]/10 border border-[#FFB300]/25 flex items-center justify-center">
              <MousePointerClick className="w-3.5 h-3.5 text-[#FFB300]" />
            </div>
            <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">Statistiques clics</p>
            <span className="ml-auto text-[10px] text-[#F5F4F1]/55 bg-[#0E0D0B] border border-warm px-2 py-0.5 rounded-full">Anti-spam actif</span>
            <button
              onClick={async () => {
                if (!window.confirm(`Réinitialiser TOUS les clics (events + counters + earnings) ?\n\nUtile pour purger les clics de test.\n\nIRRÉVERSIBLE.`)) return;
                try {
                  const res = await fetch(`${API}/admin/reset-click-stats`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
                    body: JSON.stringify({ confirm: "RESET_ALL_CLICKS" }),
                  });
                  if (res.ok) {
                    const d = await res.json();
                    toast.success(`${d.deleted_click_events} events purgés, ${d.reset_click_links} liens réinitialisés`);
                    setTimeout(() => window.location.reload(), 1500);
                  } else {
                    const e = await res.json();
                    toast.error(e.detail || "Erreur reset");
                  }
                } catch (e) { toast.error(e.message); }
              }}
              title="Reset complet des stats de clics (events + counters + earnings)"
              className="text-[10px] px-2 py-0.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 transition-all duration-150">
              Reset clics test
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {clickCards.map(c => (
              <div key={c.label} className={`${c.bg} border ${c.border} rounded-lg p-3`}>
                <c.icon className={`w-3.5 h-3.5 ${c.color} mb-2`} />
                <div className="text-base md:text-lg font-display font-semibold tabular-nums text-[#F5F4F1] tracking-tight">{typeof c.value === "number" ? c.value.toLocaleString("fr-FR") : c.value}</div>
                <div className="text-[#F5F4F1]/55 text-[10px] mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart — toggle views / clicks + period selector */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          {/* Title + loading dot */}
          <div className="flex items-center gap-2">
            <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">
              Activité <span className="text-[#F5F4F1]/40">·</span>{" "}
              <span className="text-[#F5F4F1]/55 font-normal">{PERIODS.find(p => p.key === chartPeriod)?.label}</span>
            </p>
            {timelineLoading && <div className="w-3 h-3 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />}
          </div>

          <div className="flex items-center gap-2">
            {/* Period selector + flèches */}
            {(() => {
              const idx = PERIODS.findIndex(p => p.key === chartPeriod);
              return (
                <div className="flex items-center gap-1">
                  <button onClick={() => idx > 0 && handlePeriodChange(PERIODS[idx-1].key)} disabled={idx === 0}
                    title="Période précédente"
                    className="w-6 h-6 flex items-center justify-center rounded-md text-sm font-semibold text-[#F5F4F1]/55 hover:text-[#F5F4F1] disabled:opacity-20 transition-all">‹</button>
                  <div className="flex gap-0.5 bg-[#0E0D0B] border border-warm rounded-lg p-0.5">
                    {PERIODS.map(p => (
                      <button key={p.key} onClick={() => handlePeriodChange(p.key)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all duration-150 ${chartPeriod === p.key ? "bg-[#1C1A17] text-[#F5F4F1]" : "text-[#F5F4F1]/55 hover:text-[#F5F4F1]"}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => idx < PERIODS.length-1 && handlePeriodChange(PERIODS[idx+1].key)} disabled={idx === PERIODS.length-1}
                    title="Période suivante"
                    className="w-6 h-6 flex items-center justify-center rounded-md text-sm font-semibold text-[#F5F4F1]/55 hover:text-[#F5F4F1] disabled:opacity-20 transition-all">›</button>
                </div>
              );
            })()}

            {/* Views / Clicks toggle */}
            <div className="flex gap-0.5 bg-[#0E0D0B] rounded-lg p-0.5 border border-warm">
              <button
                onClick={() => setActiveChart("views")}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 flex items-center gap-1.5 ${activeChart === "views" ? "bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/25" : "text-[#F5F4F1]/55 hover:text-[#F5F4F1] border border-transparent"}`}
              >
                <Eye className="w-3 h-3" /> Vues
              </button>
              <button
                onClick={() => setActiveChart("clicks")}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 flex items-center gap-1.5 ${activeChart === "clicks" ? "bg-[#FFB300]/15 text-[#FFB300] border border-[#FFB300]/25" : "text-[#F5F4F1]/55 hover:text-[#F5F4F1] border border-transparent"}`}
              >
                <MousePointerClick className="w-3 h-3" /> Clics
              </button>
            </div>
          </div>
        </div>
        {chartHasData ? (
          <ResponsiveContainer width="100%" height={220}>
            {activeChart === "views" ? (
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#00E5FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,244,241,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "rgba(245,244,241,0.4)", fontSize: 10 }} tickFormatter={xTickFormatter} interval={xInterval} stroke="rgba(245,244,241,0.08)" />
                <YAxis tick={{ fill: "rgba(245,244,241,0.4)", fontSize: 10 }} allowDecimals={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                  domain={[0, niceAxisMax]} allowDataOverflow={false} stroke="rgba(245,244,241,0.08)" />
                <Tooltip contentStyle={{ background: "#1C1A17", border: "1px solid rgba(245,244,241,0.12)", borderRadius: "8px", color: "#F5F4F1", fontSize: 12 }} />
                <Area type="monotone" dataKey="views" stroke="#00E5FF" fill="url(#gradViews)" strokeWidth={2} dot={false} name="Vues" />
              </AreaChart>
            ) : (
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFB300" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#FFB300" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradUniq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#39FF14" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#39FF14" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,244,241,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "rgba(245,244,241,0.4)", fontSize: 10 }} tickFormatter={xTickFormatter} interval={xInterval} stroke="rgba(245,244,241,0.08)" />
                <YAxis tick={{ fill: "rgba(245,244,241,0.4)", fontSize: 10 }} allowDecimals={false}
                  domain={[0, niceAxisMax]} allowDataOverflow={false} stroke="rgba(245,244,241,0.08)" />
                <Tooltip contentStyle={{ background: "#1C1A17", border: "1px solid rgba(245,244,241,0.12)", borderRadius: "8px", color: "#F5F4F1", fontSize: 12 }} />
                <Area type="monotone" dataKey="clicks" stroke="#FFB300" fill="url(#gradClicks)" strokeWidth={2} dot={false} name="Clics totaux" />
                <Area type="monotone" dataKey="unique_clicks" stroke="#39FF14" fill="url(#gradUniq)" strokeWidth={1.5} dot={false} strokeDasharray="4 2" name="Clics uniques" />
              </AreaChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-[220px] flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mb-3">
              {activeChart === "views" ? <Eye className="w-4 h-4 text-[#F5F4F1]/30" /> : <MousePointerClick className="w-4 h-4 text-[#F5F4F1]/30" />}
            </div>
            <p className="text-[#F5F4F1]/55 text-sm font-medium">
              {activeChart === "views" ? "Pas encore de vues" : "Pas encore de clics"}
            </p>
            <p className="text-[#F5F4F1]/35 text-xs mt-1">
              {activeChart === "views" ? "Les données apparaîtront après le premier tracking." : "Aucun clic enregistré sur la période sélectionnée."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UsersTab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBan, setConfirmBan] = useState(null);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/users")
      .then(setUsers)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (userId) => {
    try {
      await adminFetch(`/admin/users/${userId}`, { method: "DELETE" });
      toast.success("Utilisateur supprimé");
      setConfirmDelete(null);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleBan = async (userId) => {
    try {
      await adminFetch(`/admin/users/${userId}/ban`, { method: "POST" });
      toast.success("Utilisateur banni");
      setConfirmBan(null);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = users
    .filter((u) =>
      (!search ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.display_name?.toLowerCase().includes(search.toLowerCase())) &&
      (roleFilter === "all" || u.role === roleFilter)
    )
    .sort((a, b) => {
      if (sortOrder === "newest") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortOrder === "oldest") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      return 0;
    });

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
            <Users className="w-4.5 h-4.5 text-[#00E5FF]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Utilisateurs</h2>
          <span className="text-[#F5F4F1]/40 text-sm tabular-nums ml-1">({filtered.length}/{users.length})</span>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Tous les comptes inscrits — modération, bannissement, suppression.</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex gap-2.5 flex-wrap flex-1">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher email, nom…"
            className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22 w-56"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm focus:outline-none focus:border-[#F5F4F1]/22 cursor-pointer"
          >
            <option value="all">Tous les rôles</option>
            <option value="clipper">Clippeur</option>
            <option value="agency">Agence</option>
            <option value="manager">Manager</option>
            <option value="client">Client</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm focus:outline-none focus:border-[#F5F4F1]/22 cursor-pointer"
          >
            <option value="newest">Plus récents</option>
            <option value="oldest">Plus anciens</option>
          </select>
        </div>
        <button onClick={fetchUsers} title="Recharger la liste" className="p-2 bg-[#1C1A17] hover:bg-[#262320] border border-warm rounded-lg text-[#F5F4F1]/70 hover:text-[#F5F4F1] transition-all duration-150">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm text-[#F5F4F1]/55 text-[11px] uppercase tracking-wider">
                  <th className="text-left py-3 px-4 font-medium">Nom</th>
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium">Rôle</th>
                  <th className="text-left py-3 px-4 font-medium">Inscription</th>
                  <th className="text-left py-3 px-4 font-medium">Statut</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.user_id} className="border-b border-warm last:border-b-0 transition-colors">
                    <td className="py-3 px-4 text-[#F5F4F1] font-medium">
                      {user.display_name || user.name || "—"}
                    </td>
                    <td className="py-3 px-4 text-[#F5F4F1]/65 text-xs">{user.email}</td>
                    <td className="py-3 px-4">{roleBadge(user.role)}</td>
                    <td className="py-3 px-4 text-[#F5F4F1]/45 text-xs tabular-nums">{formatDate(user.created_at)}</td>
                    <td className="py-3 px-4">
                      {user.banned ? (
                        <span className="inline-flex items-center gap-1.5 text-red-400 text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                          Banni
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[#39FF14] text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]" />
                          Actif
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1.5 justify-end">
                        <button
                          onClick={() => setConfirmBan(user)}
                          className="p-1.5 rounded-md bg-[#FFB300]/10 hover:bg-[#FFB300]/20 text-[#FFB300] border border-[#FFB300]/20 transition-all duration-150"
                          title="Bannir cet utilisateur"
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(user)}
                          className="p-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all duration-150"
                          title="Supprimer définitivement"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mb-3">
                          <Users className="w-4 h-4 text-[#F5F4F1]/30" />
                        </div>
                        <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun utilisateur</p>
                        <p className="text-[#F5F4F1]/35 text-xs mt-1">Aucun résultat avec les filtres actuels.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer l'utilisateur ?"
          message={`Supprimer « ${confirmDelete.display_name || confirmDelete.name} » (${confirmDelete.email}) et toutes ses données ?`}
          confirmLabel="Supprimer définitivement"
          danger
          onConfirm={() => handleDelete(confirmDelete.user_id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Confirm Ban */}
      {confirmBan && (
        <ConfirmModal
          title="Bannir l'utilisateur ?"
          message={`Bannir « ${confirmBan.display_name || confirmBan.name} » et révoquer toutes ses sessions ?`}
          confirmLabel="Bannir"
          onConfirm={() => handleBan(confirmBan.user_id)}
          onCancel={() => setConfirmBan(null)}
        />
      )}
    </div>
  );
}

// ─── PreviewTab ───────────────────────────────────────────────────────────────

function PreviewTab({ role, label, icon: Icon, color }) {
  const [loading, setLoading] = useState(false);

  const openPreview = async () => {
    setLoading(true);
    try {
      // This call sets the session_token cookie automatically (credentials: include)
      await adminFetch(`/admin/demo-login/${role}`, { method: "POST" });
      // Cookie is now set — open the dashboard in a new tab, it will be auto-authenticated
      window.open(`/${role}`, "_blank");
      toast.success(`Preview ${label} ouvert — connecté en tant que démo`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const roleDescriptions = {
    clipper: "Voir l'app comme un clippeur — accès aux campagnes, comptes sociaux, gains et chat.",
    agency: "Voir l'app comme une agence — création de campagnes, gestion des clippers, analytics.",
    manager: "Voir l'app comme un manager — supervision des clippers, conseils, modération.",
    client: "Voir l'app comme un client — suivi des campagnes, validation de contenus, toutes les campagnes.",
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
            <Icon className="w-4.5 h-4.5 text-[#FF007F]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Preview — {label}</h2>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Voir l'application comme la verrait un compte {label.toLowerCase()}.</p>
      </div>

      <div className="max-w-xl space-y-4">
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-6 md:p-8">
          <div className={`w-14 h-14 rounded-2xl ${color} border border-warm flex items-center justify-center mb-5`}>
            <Icon className="w-7 h-7 text-[#F5F4F1]" />
          </div>
          <h3 className="text-lg font-display font-semibold tracking-tight text-[#F5F4F1] mb-2">Dashboard {label}</h3>
          <p className="text-[#F5F4F1]/60 text-sm mb-6 leading-relaxed">{roleDescriptions[role]}</p>

          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-4 mb-6 text-xs space-y-1">
            <p className="font-display font-semibold text-[#F5F4F1] mb-2 text-[13px]">Compte de démonstration</p>
            <p className="text-[#F5F4F1]/55">Email <span className="text-[#F5F4F1]/80 font-mono ml-1">{role}@demo.clipdeal.local</span></p>
            <p className="text-[#F5F4F1]/55">Rôle <span className="text-[#F5F4F1]/80 font-mono ml-1">{role}</span></p>
            <p className="mt-3 text-[#FFB300] text-[11px] flex items-start gap-1.5">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Session preview valable 24 h. Le clic droit admin est actif dans ce contexte.</span>
            </p>
          </div>

          <button
            onClick={openPreview}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#F5F4F1] hover:bg-white disabled:opacity-50 text-[#0E0D0B] font-semibold px-6 py-2.5 rounded-lg transition-all duration-150"
          >
            <ExternalLink className="w-4 h-4" />
            {loading ? "Connexion…" : `Ouvrir le dashboard ${label}`}
          </button>
        </div>

        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
          <p className="text-[11px] text-[#F5F4F1]/55 mb-2 uppercase tracking-widest font-medium">Clic droit admin</p>
          <p className="text-sm text-[#F5F4F1]/70 leading-relaxed">
            Dans les pages preview, fais un <strong className="text-[#F5F4F1]">clic droit</strong> sur une
            campagne ou une vidéo pour accéder aux options de modération admin (suppression + message automatique).
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── ApiStatusTab ─────────────────────────────────────────────────────────────

// ─── Scraping History (logs des appels par source — alerte si Apify) ──────

// Classifie une entree scraping_history en categorie metier :
//   ok          → vrai succes, vidéos récupérées
//   alive_no_scrape → compte vivant confirme par verif active mais scrape rate (PAS un vrai fail)
//   empty_account → scrape OK mais 0 video recente (compte sans publi récente / privé sans contenu)
//   private     → compte prive (no public content)
//   deleted     → compte supprime / introuvable
//   ko_source   → vrai fail technique (rate-limited, source down, network)
//   partial     → succes partiel (< seuil de videos, ou views=0)
// Utilise result_type backend si dispo (futur), sinon fallback heuristique sur (source, success, error).
// Mapping result_type canonique (backend CLASSIFIER) -> categorie UI
// Backend renvoie : videos_found, account_private, account_empty, account_deleted,
//                   rate_limited, source_error
// UI utilise      : ok, alive_no_scrape, empty_account, private, deleted, ko_source, partial
const RESULT_TYPE_TO_CATEGORY = {
  videos_found:    "ok",
  account_private: "private",
  account_empty:   "empty_account",
  account_deleted: "deleted",
  rate_limited:    "alive_no_scrape",  // rate-limited = source rate, compte presume vivant
  source_error:    "ko_source",
};

function classifyScrapeEntry(h) {
  // 1) Si le backend renseigne result_type (champ CLASSIFIER), on le mappe vers la categorie UI
  if (h.result_type && RESULT_TYPE_TO_CATEGORY[h.result_type]) {
    return RESULT_TYPE_TO_CATEGORY[h.result_type];
  }

  const src = (h.source || "").toLowerCase();
  const err = (h.error || "").toLowerCase();

  // 2) Sources speciales semantiques (deja loguees par server.py)
  if (src === "active_verification_exists_scrape_failed") return "alive_no_scrape";
  if (src === "scrape_ok_zero_videos") return "empty_account";
  if (src.endsWith("_partial") || src === "tikwm_partial") return "partial";

  // 3) Heuristique sur error pour les vrais fails
  if (!h.success) {
    if (err.includes("not found") || err.includes("introuvable") || err.includes("404") || err.includes("deleted")) return "deleted";
    if (err.includes("private") || err.includes("prive")) return "private";
    if (err.includes("all views=0") || err.includes("partial")) return "partial";
    return "ko_source";
  }

  // 4) Success avec 0 vidéos = compte vide
  if (h.success && (h.video_count || 0) === 0) return "empty_account";

  return "ok";
}

// Style + label par categorie
const CATEGORY_STYLE = {
  ok:               { label: "OK",                       dot: "bg-[#39FF14]",       text: "text-[#39FF14]",  bg: "bg-[#39FF14]/10",  border: "border-[#39FF14]/25" },
  alive_no_scrape:  { label: "Vivant (scrape rate)",     dot: "bg-[#00E5FF]",       text: "text-[#00E5FF]",  bg: "bg-[#00E5FF]/10",  border: "border-[#00E5FF]/25" },
  empty_account:    { label: "0 vidéo publique",         dot: "bg-[#FFB300]",       text: "text-[#FFB300]",  bg: "bg-[#FFB300]/10",  border: "border-[#FFB300]/25" },
  private:          { label: "Compte privé",             dot: "bg-[#FFB300]",       text: "text-[#FFB300]",  bg: "bg-[#FFB300]/10",  border: "border-[#FFB300]/25" },
  deleted:          { label: "Supprimé",                 dot: "bg-[#F5F4F1]/40",    text: "text-[#F5F4F1]/55", bg: "bg-[#0E0D0B]",   border: "border-warm" },
  ko_source:        { label: "Échec source",             dot: "bg-red-400",         text: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/25" },
  partial:          { label: "Partiel",                  dot: "bg-[#FF007F]",       text: "text-[#FF007F]",  bg: "bg-[#FF007F]/10",  border: "border-[#FF007F]/25" },
};

function ScrapingHistoryTab() {
  const [data, setData] = useState(null);
  const [resultStats, setResultStats] = useState(null); // optionnel : endpoint /admin/scrape-result-stats si dispo
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | apify_only | source-X
  const [categoryFilter, setCategoryFilter] = useState("all"); // all | ok | ko_source | ...
  const [search, setSearch] = useState("");

  const refresh = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "200" });
    if (filter === "apify_only") params.set("apify_only", "true");
    else if (filter !== "all") params.set("source", filter);
    adminFetch(`/admin/scraping-history?${params.toString()}`)
      .then(setData)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));

    // Best-effort : endpoint futur cree par agent CLASSIFIER, on ignore 404
    adminFetch(`/admin/scrape-result-stats?hours=24`)
      .then(setResultStats)
      .catch(() => { /* endpoint pas encore deploye, on degrade silencieusement */ });
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!data && loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" /></div>;
  }
  if (!data) return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-8 text-center">
      <p className="text-[#F5F4F1]/55 text-sm">Aucune donnée.</p>
      <button onClick={refresh} className="mt-3 text-[#00E5FF] text-xs hover:underline">Réessayer</button>
    </div>
  );

  const SOURCE_COLORS = {
    clipscraper: "text-[#39FF14]",
    apify: "text-red-400",
    tikwm: "text-[#00E5FF]",
    tikwm_partial: "text-[#00E5FF]/70",
    rapidapi: "text-[#FFB300]",
    instagram_private: "text-[#FF007F]",
    instaloader: "text-[#FF007F]/80",
    playwright: "text-[#00E5FF]/80",
    ytdlp: "text-[#FFB300]/80",
    youtube_api: "text-red-400/80",
    tiktok_mobile: "text-[#00E5FF]/80",
    active_verification_exists_scrape_failed: "text-[#00E5FF]/70",
    scrape_ok_zero_videos: "text-[#FFB300]/80",
    business_discovery: "text-[#39FF14]",
    instagram_public_html: "text-[#FF007F]/80",
    ytdlp_insta: "text-[#FF007F]/70",
    ytdlp_fallback: "text-[#FFB300]/60",
    clips_graphql: "text-[#FF007F]/70",
    vps_graphql: "text-[#00E5FF]/70",
    auto_archive: "text-[#F5F4F1]/55",
  };

  const SOURCE_LABEL = {
    clipscraper: "ClipScraper VPS",
    apify: "🚨 Apify",
    tikwm: "TikWm",
    tikwm_partial: "TikWm partial",
    rapidapi: "RapidAPI",
    instagram_private: "Insta Private",
    instaloader: "Instaloader",
    playwright: "Playwright",
    ytdlp: "yt-dlp",
    youtube_api: "YouTube API",
    tiktok_mobile: "TikTok Mobile",
    active_verification_exists_scrape_failed: "Vivant (verif active)",
    scrape_ok_zero_videos: "Scrape OK, 0 vidéo",
    business_discovery: "Meta Business",
    instagram_public_html: "IG public HTML",
    ytdlp_insta: "yt-dlp Insta",
    ytdlp_fallback: "yt-dlp (fallback)",
    clips_graphql: "Clips GraphQL",
    vps_graphql: "VPS GraphQL",
    auto_archive: "Auto-archive",
  };

  // ─── Calcul du summary global par compte (la vraie metric pour Paul) ──────
  // Pour chaque (platform, username), on prend la categorie de la DERNIERE entree.
  // Ca donne le statut COURANT de chaque compte (pas le bruit detaille des retries).
  const history = data.history || [];
  const accountStatus = new Map(); // key = "platform:username" → category
  // history est deja trie DESC par timestamp donc le premier vu = le plus recent
  for (const h of history) {
    const key = `${h.platform || "?"}:${h.username || "?"}`;
    if (!accountStatus.has(key)) {
      accountStatus.set(key, classifyScrapeEntry(h));
    }
  }

  // Si l'endpoint /admin/scrape-result-stats existe et renvoie des donnees, on prefere
  // ses chiffres (calcules cote backend sur la fenetre complete, pas juste l'echantillon de 200)
  let summary;
  if (resultStats && typeof resultStats === "object" && Array.isArray(resultStats.by_result_type)) {
    // Le backend renvoie by_result_type : [{result_type, count, videos_total, successes}, ...]
    // On agrege en categories UI via RESULT_TYPE_TO_CATEGORY.
    const counts = { ok: 0, alive_no_scrape: 0, empty_account: 0, private: 0, deleted: 0, ko_source: 0, partial: 0 };
    for (const row of resultStats.by_result_type) {
      const cat = RESULT_TYPE_TO_CATEGORY[row.result_type] || "ko_source";
      counts[cat] = (counts[cat] || 0) + (row.count || 0);
    }
    summary = {
      ...counts,
      total: resultStats.total_events || 0,
      source: "backend",
    };
  } else {
    // Fallback : on agrege depuis l'echantillon en memoire
    const counts = { ok: 0, alive_no_scrape: 0, empty_account: 0, private: 0, deleted: 0, ko_source: 0, partial: 0 };
    for (const cat of accountStatus.values()) {
      if (counts[cat] != null) counts[cat] += 1;
    }
    summary = { ...counts, total: accountStatus.size, source: "fallback" };
  }
  // "Vraiment OK" = comptes qui ramenent du contenu OU sont vivants OU sont vides (legitimement)
  // Les seuls vrais problemes : ko_source + partial.
  const healthyCount = summary.ok + summary.alive_no_scrape + summary.empty_account;
  const problemCount = summary.ko_source + summary.partial;
  const totalAccounts = summary.total || (healthyCount + problemCount + summary.private + summary.deleted);
  const healthRate = totalAccounts > 0 ? Math.round((healthyCount / totalAccounts) * 100) : 0;

  // Filter by search + category
  const filteredHistory = history.filter(h => {
    if (categoryFilter !== "all" && classifyScrapeEntry(h) !== categoryFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (h.username || "").toLowerCase().includes(s) || (h.platform || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
              <RefreshCw className="w-4.5 h-4.5 text-[#FF007F]" />
            </div>
            <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Historique scraping</h2>
          </div>
          <p className="text-[#F5F4F1]/55 text-sm">Toutes les tentatives de scraping triées par source. Apify est en rouge.</p>
        </div>
        <button onClick={refresh} disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1] text-sm transition-all duration-150 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      {/* ─── SUMMARY GLOBAL (prime sur le detail anxiogene) ────────────────── */}
      <div className={`rounded-xl p-5 border ${
        healthRate >= 80 ? "bg-[#39FF14]/8 border-[#39FF14]/30" :
        healthRate >= 50 ? "bg-[#FFB300]/8 border-[#FFB300]/30" :
        "bg-red-500/8 border-red-500/30"
      }`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <p className="text-[#F5F4F1]/60 text-[11px] uppercase tracking-widest font-medium">Santé globale des scrapes</p>
            <div className="flex items-baseline gap-3 mt-1.5 flex-wrap">
              <span className={`text-3xl md:text-4xl font-display font-semibold tabular-nums tracking-tight ${
                healthRate >= 80 ? "text-[#39FF14]" :
                healthRate >= 50 ? "text-[#FFB300]" : "text-red-400"
              }`}>{healthyCount}/{totalAccounts}</span>
              <span className="text-[#F5F4F1]/70 text-sm">comptes en bonne santé</span>
              <span className={`text-xl md:text-2xl font-mono font-semibold tabular-nums ${
                healthRate >= 80 ? "text-[#39FF14]" :
                healthRate >= 50 ? "text-[#FFB300]" : "text-red-400"
              }`}>{healthRate}%</span>
            </div>
            <p className="text-[#F5F4F1]/55 text-xs mt-2 leading-relaxed">
              "Sain" = scrape OK <span className="text-[#F5F4F1]/30">·</span> compte vivant confirmé même si proxy rate <span className="text-[#F5F4F1]/30">·</span> compte légitimement vide.<br/>
              Les comptes privés/supprimés sont comptés à part — ce n'est pas un fail technique.
            </p>
          </div>
          {/* Mini-tuiles par categorie */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1 min-w-[280px]">
            {[
              { key: "ok",              label: "Scrape OK",      val: summary.ok },
              { key: "alive_no_scrape", label: "Vivant rate",    val: summary.alive_no_scrape },
              { key: "empty_account",   label: "0 vidéo",        val: summary.empty_account },
              { key: "private",         label: "Privé",          val: summary.private },
              { key: "deleted",         label: "Supprimé",       val: summary.deleted },
              { key: "partial",         label: "Partiel",        val: summary.partial },
              { key: "ko_source",       label: "Échec source",   val: summary.ko_source },
            ].filter(t => t.val > 0).map(t => {
              const style = CATEGORY_STYLE[t.key];
              return (
                <button key={t.key} onClick={() => setCategoryFilter(categoryFilter === t.key ? "all" : t.key)}
                  title={`Filtrer par ${style.label}`}
                  className={`rounded-lg px-3 py-2 border text-left transition-all duration-150 ${style.bg} ${style.border} ${categoryFilter === t.key ? "ring-2 ring-[#F5F4F1]/30" : "hover:brightness-125"}`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${style.dot}`}/>
                    <span className={`text-[10px] uppercase font-semibold tracking-wider ${style.text}`}>{t.label}</span>
                  </div>
                  <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-xl mt-1">{t.val}</p>
                </button>
              );
            })}
          </div>
        </div>
        {summary.source === "fallback" && (
          <p className="text-[#F5F4F1]/40 text-[10px] mt-3 leading-relaxed">
            Stats calculées sur l'échantillon en mémoire ({history.length} entrées). Quand l'endpoint <code className="text-[#F5F4F1]/65 font-mono">/admin/scrape-result-stats</code> sera déployé, ces chiffres seront calculés sur la fenêtre 24 h complète côté backend.
          </p>
        )}
      </div>

      {/* Alerte rouge si Apify utilisé */}
      {data.apify_today_count > 0 && (
        <div className="bg-red-500/8 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4.5 h-4.5 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-red-400 font-display font-semibold text-base tracking-tight">Apify utilisé aujourd'hui</p>
              <p className="text-[#F5F4F1]/80 text-sm mt-1 tabular-nums">
                <span className="font-display font-semibold text-red-400 text-lg">{data.apify_today_count}</span> appels aujourd'hui
                <span className="mx-2 text-[#F5F4F1]/25">·</span>
                <span className="font-display font-semibold text-red-400 text-lg">{data.apify_month_count}</span> ce mois ({data.apify_month_videos} vidéos)
              </p>
              <p className="text-[#F5F4F1]/55 text-xs mt-2 leading-relaxed">
                Apify est censé être le dernier recours. Si ce compteur monte vite, c'est que ClipScraper VPS et les autres sources échouent — vérifie la config <code className="text-[#F5F4F1] font-mono">CLIP_SCRAPER_URL/KEY</code> sur Railway et l'état du VPS.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats par source sur 24h */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
        <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight mb-3">Sources utilisées (dernières 24 h)</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {(data.stats_24h || []).map(s => {
            const isApify = s._id?.startsWith("apify");
            return (
              <div key={s._id} className={`rounded-lg p-3 border ${isApify ? "bg-red-500/8 border-red-500/25" : "bg-[#0E0D0B] border-warm"}`}>
                <p className={`text-[11px] font-medium mb-1 truncate ${SOURCE_COLORS[s._id] || "text-[#F5F4F1]/60"}`}>
                  {SOURCE_LABEL[s._id] || s._id}
                </p>
                <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-lg tracking-tight">{s.count}</p>
                <p className="text-[10px] text-[#F5F4F1]/45 tabular-nums">{s.successes} OK <span className="text-[#F5F4F1]/25">·</span> {s.videos_total} vidéos</p>
              </div>
            );
          })}
          {(!data.stats_24h || data.stats_24h.length === 0) && (
            <p className="text-[#F5F4F1]/40 text-sm col-span-full">Aucune activité de scraping dans les 24 h.</p>
          )}
        </div>
      </div>

      {/* Filtres + recherche */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-[#0E0D0B] border border-warm rounded-lg p-1 gap-0.5">
          {[
            { id: "all", label: "Toutes sources" },
            { id: "apify_only", label: "Apify seulement" },
            { id: "clipscraper", label: "ClipScraper" },
            { id: "tikwm", label: "TikWm" },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 ${filter === f.id ? (f.id === "apify_only" ? "bg-red-500/25 text-red-400 border border-red-500/30" : "bg-[#1C1A17] text-[#F5F4F1] border border-warm") : "text-[#F5F4F1]/55 hover:text-[#F5F4F1] border border-transparent"}`}>
              {f.label}
            </button>
          ))}
        </div>
        {categoryFilter !== "all" && (
          <button onClick={() => setCategoryFilter("all")}
            title="Retirer le filtre catégorie"
            className="px-2.5 py-1 rounded-md bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1]/70 text-xs flex items-center gap-1.5 transition-all duration-150">
            <span className={`w-1.5 h-1.5 rounded-full ${CATEGORY_STYLE[categoryFilter]?.dot || "bg-[#F5F4F1]/40"}`}/>
            {CATEGORY_STYLE[categoryFilter]?.label || categoryFilter}
            <X className="w-3 h-3" />
          </button>
        )}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher username ou plateforme…"
          className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22 flex-1 max-w-xs" />
        <span className="text-[#F5F4F1]/45 text-xs tabular-nums">{filteredHistory.length} / {data.total} entrées</span>
      </div>

      {/* Table historique */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm text-[#F5F4F1]/55 text-[11px] uppercase tracking-wider">
                <th className="text-left py-3 px-4 font-medium">Date</th>
                <th className="text-left py-3 px-4 font-medium">Source</th>
                <th className="text-left py-3 px-4 font-medium">Plateforme</th>
                <th className="text-left py-3 px-4 font-medium">Compte</th>
                <th className="text-left py-3 px-4 font-medium">Catégorie</th>
                <th className="text-left py-3 px-4 font-medium">Vidéos</th>
                <th className="text-left py-3 px-4 font-medium">Détail</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((h) => {
                const isApify = h.is_apify;
                const dt = new Date(h.timestamp);
                const cat = classifyScrapeEntry(h);
                const style = CATEGORY_STYLE[cat] || CATEGORY_STYLE.ko_source;
                return (
                  <tr key={h.id} className={`border-b border-warm last:border-b-0 ${isApify ? "bg-red-500/5" : ""} transition-colors`}>
                    <td className="py-2.5 px-4 text-[#F5F4F1]/70 text-xs whitespace-nowrap tabular-nums">
                      {dt.toLocaleDateString("fr-FR")} {dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className={`py-2.5 px-4 font-medium text-xs ${SOURCE_COLORS[h.source] || "text-[#F5F4F1]/65"} ${isApify ? "font-semibold" : ""}`}>
                      {SOURCE_LABEL[h.source] || h.source}
                    </td>
                    <td className="py-2.5 px-4 text-[#F5F4F1]/70 text-xs">
                      {h.platform === "tiktok" ? "🎵" : h.platform === "instagram" ? "📸" : h.platform === "youtube" ? "▶️" : ""} {h.platform}
                    </td>
                    <td className="py-2.5 px-4 text-[#F5F4F1] text-xs">@{h.username}</td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium ${style.bg} ${style.border} ${style.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}/>
                        {style.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 font-mono text-[#F5F4F1]/70 text-xs tabular-nums">{h.video_count || 0}</td>
                    <td className="py-2.5 px-4 text-[#F5F4F1]/45 text-[11px] max-w-[300px] truncate" title={h.error}>
                      {h.error || "—"}
                    </td>
                  </tr>
                );
              })}
              {filteredHistory.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mb-3">
                      <RefreshCw className="w-4 h-4 text-[#F5F4F1]/30" />
                    </div>
                    <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucune entrée</p>
                    <p className="text-[#F5F4F1]/35 text-xs mt-1">Aucun résultat avec les filtres actuels.</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Alertes Fraude (compte revendiqué par 2 users, fake views, bot clicks) ─────
function FraudAlertsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");

  const refresh = useCallback(() => {
    setLoading(true);
    adminFetch(`/admin/fraud-alerts?status=${statusFilter}`)
      .then(setData)
      .catch(() => toast.error("Erreur chargement alertes"))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const resolve = async (alertId, decision) => {
    if (!window.confirm(`Marquer cette alerte comme "${decision}" ?`)) return;
    try {
      await adminFetch(`/admin/fraud-alerts/${alertId}/resolve`, {
        method: "POST",
        body: JSON.stringify({ decision, note: "" }),
      });
      toast.success("Alerte résolue");
      refresh();
    } catch (e) { toast.error(e.message || "Erreur réseau"); }
  };

  const sevColor = (sev) => sev === "high" ? "text-red-400" : sev === "medium" ? "text-[#FFB300]" : "text-[#F5F4F1]/60";
  const sevBg = (sev) => sev === "high" ? "bg-red-500/8 border-red-500/25" : sev === "medium" ? "bg-[#FFB300]/8 border-[#FFB300]/25" : "bg-[#0E0D0B] border-warm";

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
            <AlertTriangle className="w-4.5 h-4.5 text-red-400" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Alertes fraude</h2>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Détection automatique : comptes revendiqués par plusieurs utilisateurs, fake views, bot clicks.</p>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="flex bg-[#0E0D0B] border border-warm rounded-lg p-1 gap-0.5">
          {["pending", "resolved", "all"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 ${statusFilter === s ? "bg-red-500/15 text-red-400 border border-red-500/25" : "text-[#F5F4F1]/55 hover:text-[#F5F4F1] border border-transparent"}`}>
              {s === "pending" ? "En attente" : s === "resolved" ? "Résolues" : "Toutes"}
            </button>
          ))}
        </div>
        <button onClick={refresh} disabled={loading}
          title="Rafraîchir la liste"
          className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1]/70 hover:text-[#F5F4F1] text-xs transition-all duration-150 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Chargement…" : "Rafraîchir"}
        </button>
      </div>

      {loading && <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" /></div>}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-wider">Stockées</p>
              <p className="text-2xl font-display font-semibold tabular-nums text-[#F5F4F1] tracking-tight mt-1">{data.stored_alerts_count}</p>
            </div>
            <div className="bg-[#1C1A17]/85 border border-[#FFB300]/25 rounded-xl p-4">
              <p className="text-[11px] text-[#FFB300]/80 uppercase tracking-wider">Détectées (live)</p>
              <p className="text-2xl font-display font-semibold tabular-nums text-[#FFB300] tracking-tight mt-1">{data.live_alerts_count}</p>
            </div>
            <div className="bg-[#1C1A17]/85 border border-red-500/25 rounded-xl p-4">
              <p className="text-[11px] text-red-400/80 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-display font-semibold tabular-nums text-red-400 tracking-tight mt-1">{data.total}</p>
            </div>
          </div>

          {/* Stockées */}
          {data.stored_alerts?.length > 0 && (
            <div>
              <h3 className="text-[11px] font-display font-semibold tracking-tight text-[#F5F4F1]/70 mb-2 uppercase tracking-wider">Alertes stockées ({data.stored_alerts.length})</h3>
              <div className="space-y-2">
                {data.stored_alerts.map(a => (
                  <div key={a.alert_id} className={`rounded-xl p-4 border ${sevBg(a.severity)}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className={`text-[10px] font-display font-semibold uppercase tracking-wider ${sevColor(a.severity)}`}>{a.severity}</span>
                          <span className="text-[#F5F4F1]/45 text-xs">{a.type}</span>
                          {a.platform && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[#0E0D0B] border border-warm text-[#F5F4F1]/60">{a.platform}</span>}
                        </div>
                        <p className="text-[#F5F4F1] text-sm leading-relaxed">{a.details}</p>
                        <div className="text-[#F5F4F1]/45 text-xs mt-2 space-y-0.5">
                          {a.username && <p>Compte @{a.username}</p>}
                          {a.existing_user_email && <p>Existant <span className="text-[#F5F4F1]/65 ml-1">{a.existing_user_name || a.existing_user_email}</span></p>}
                          {a.attempted_user_email && <p>Tentative <span className="text-[#F5F4F1]/65 ml-1">{a.attempted_user_name || a.attempted_user_email}</span></p>}
                          {a.campaign_name && <p>Campagne <span className="text-[#F5F4F1]/65 ml-1">{a.campaign_name}</span></p>}
                          <p className="text-[#F5F4F1]/35 tabular-nums mt-1">{new Date(a.created_at).toLocaleString("fr-FR")}</p>
                        </div>
                      </div>
                      {a.status === "pending" && (
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button onClick={() => resolve(a.alert_id, "confirmed")}
                            className="px-2.5 py-1 rounded-md text-[10px] bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/25 font-medium transition-all duration-150">
                            Confirmer fraude
                          </button>
                          <button onClick={() => resolve(a.alert_id, "false_positive")}
                            className="px-2.5 py-1 rounded-md text-[10px] bg-[#0E0D0B] hover:bg-[#262320] border border-warm text-[#F5F4F1]/65 transition-all duration-150">
                            Faux positif
                          </button>
                        </div>
                      )}
                      {a.status === "resolved" && (
                        <span className={`px-2 py-1 rounded-md text-[10px] font-medium border ${a.decision === "confirmed" ? "bg-red-500/15 text-red-400 border-red-500/25" : "bg-[#0E0D0B] text-[#F5F4F1]/55 border-warm"}`}>
                          {a.decision === "confirmed" ? "Fraude" : "Faux positif"}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live detected */}
          {data.live_detected_alerts?.length > 0 && (
            <div>
              <h3 className="text-[11px] font-display font-semibold tracking-tight text-[#F5F4F1]/70 mb-2 uppercase tracking-wider">Détection live ({data.live_detected_alerts.length})</h3>
              <div className="space-y-2">
                {data.live_detected_alerts.map((a, i) => (
                  <div key={i} className={`rounded-xl p-3 border ${sevBg(a.severity)}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-display font-semibold uppercase tracking-wider ${sevColor(a.severity)}`}>{a.severity}</span>
                      <span className="text-[#F5F4F1]/45 text-xs">{a.type}</span>
                    </div>
                    <p className="text-[#F5F4F1] text-sm leading-relaxed">{a.details}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.total === 0 && (
            <div className="text-center py-16 bg-[#1C1A17]/85 border border-warm rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/25 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6 text-[#39FF14]" />
              </div>
              <p className="text-[#F5F4F1] text-sm font-display font-semibold tracking-tight">Aucune alerte de fraude</p>
              <p className="text-[#F5F4F1]/45 text-xs mt-1">Le système surveille automatiquement les patterns suspects.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Prospects (campagnes pre-remplies pour demarcher agences) ─────
function ProspectsTab() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newCamp, setNewCamp] = useState({
    // basics
    name: "", description: "", agency_name: "", image_url: "",
    payment_model: "views",
    rpm: 5, rate_per_click: 5, destination_url: "",
    click_billing_mode: "unique_24h", click_window_hours: 24,
    // platforms
    platforms: ["tiktok", "instagram", "youtube"],
    // règles
    max_clippers: "", cadence: 1, max_strikes: 3, strike_days: 3,
    min_view_payout: 0, max_view_payout: "",
    // budget
    budget_unlimited: true, budget_total: 0,
    // candidature
    application_form_enabled: true,
    application_questions: ["Pourquoi veux-tu rejoindre cette campagne ?"],
    // tracking
    tracking_start_date: "",
  });
  const [addingClipper, setAddingClipper] = useState(null);
  const [bulkClipper, setBulkClipper] = useState({ discord_username: "", accounts: [{ platform: "tiktok", username: "" }] });
  const [accountsMap, setAccountsMap] = useState({}); // { [campaign_id]: { by_discord: {...} } }

  const baseUrl = window.location.origin;

  const fetchAccountsFor = useCallback(async (cid) => {
    try {
      const d = await adminFetch(`/admin/prospects/${cid}/clipper-accounts`);
      setAccountsMap(prev => ({ ...prev, [cid]: d }));
    } catch {}
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/prospects").then(d => {
      setProspects(d.prospects || []);
      // Auto-fetch accounts pour chaque
      (d.prospects || []).forEach(p => fetchAccountsFor(p.campaign_id));
    }).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [fetchAccountsFor]);
  useEffect(() => { refresh(); }, [refresh]);

  const createCampaign = async () => {
    if (!newCamp.name || !newCamp.agency_name) { toast.error("Nom + agence requis"); return; }
    if ((newCamp.payment_model === "views" || newCamp.payment_model === "both") && !newCamp.rpm) {
      toast.error("RPM requis pour le modèle vues"); return;
    }
    if ((newCamp.payment_model === "clicks" || newCamp.payment_model === "both") && (!newCamp.rate_per_click || !newCamp.destination_url.trim())) {
      toast.error("Tarif clic + URL destination requis"); return;
    }
    if (newCamp.platforms.length === 0) {
      toast.error("Sélectionne au moins 1 plateforme"); return;
    }
    try {
      const payload = {
        ...newCamp,
        rpm: parseFloat(newCamp.rpm) || 0,
        rate_per_click: parseFloat(newCamp.rate_per_click) || 0,
        max_clippers: newCamp.max_clippers ? parseInt(newCamp.max_clippers) : null,
        max_view_payout: newCamp.max_view_payout ? parseInt(newCamp.max_view_payout) : null,
        budget_total: parseFloat(newCamp.budget_total) || 0,
        application_questions: newCamp.application_form_enabled
          ? (newCamp.application_questions || []).filter(q => q && q.trim())
          : [],
        tracking_start_date: newCamp.tracking_start_date
          ? new Date(newCamp.tracking_start_date + "T00:00:00Z").toISOString()
          : null,
      };
      const res = await fetch(`${API}/admin/prospects/create-campaign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Campagne prospect créée ✓");
        setShowCreate(false);
        setNewCamp({
          name: "", description: "", agency_name: "", image_url: "",
          payment_model: "views",
          rpm: 5, rate_per_click: 5, destination_url: "",
          click_billing_mode: "unique_24h", click_window_hours: 24,
          platforms: ["tiktok", "instagram", "youtube"],
          max_clippers: "", cadence: 1, max_strikes: 3, strike_days: 3,
          min_view_payout: 0, max_view_payout: "",
          budget_unlimited: true, budget_total: 0,
          application_form_enabled: true,
          application_questions: ["Pourquoi veux-tu rejoindre cette campagne ?"],
          tracking_start_date: "",
        });
        refresh();
      } else { const e = await res.json(); toast.error(e.detail || "Erreur"); }
    } catch (e) { toast.error(e.message); }
  };

  const togglePlatform = (p) => {
    setNewCamp(prev => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p]
    }));
  };

  const submitBulkClipper = async (cid) => {
    if (!bulkClipper.discord_username) { toast.error("Pseudo Discord requis"); return; }
    const valid = bulkClipper.accounts.filter(a => a.username && a.username.trim());
    if (valid.length === 0) { toast.error("Au moins 1 compte requis"); return; }
    let okCount = 0;
    for (const acc of valid) {
      try {
        const res = await fetch(`${API}/admin/prospects/${cid}/add-clipper-account`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
          body: JSON.stringify({ discord_username: bulkClipper.discord_username, platform: acc.platform, username: acc.username.trim().replace(/^@/, "") }),
        });
        if (res.ok) okCount++;
      } catch {}
    }
    if (okCount > 0) {
      toast.success(`✓ ${okCount}/${valid.length} compte(s) ajouté(s) pour ${bulkClipper.discord_username}`);
      setBulkClipper({ discord_username: "", accounts: [{ platform: "tiktok", username: "" }] });
      setAddingClipper(null);
      fetchAccountsFor(cid);
      refresh();
    } else {
      toast.error("Aucun compte ajouté");
    }
  };

  const deleteProspectAccount = async (account_id, cid) => {
    if (!window.confirm("Supprimer ce compte pré-enregistré ?")) return;
    try {
      const res = await fetch(`${API}/admin/prospects/clipper-accounts/${account_id}`, {
        method: "DELETE",
        headers: { "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
      });
      if (res.ok) {
        toast.success("Supprimé");
        fetchAccountsFor(cid);
      } else { toast.error("Erreur"); }
    } catch (e) { toast.error(e.message); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
              <Building2 className="w-4.5 h-4.5 text-[#FF007F]" />
            </div>
            <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Prospects</h2>
          </div>
          <p className="text-[#F5F4F1]/55 text-sm">Crée des campagnes pré-remplies pour démarcher des agences.</p>
        </div>
        <button onClick={() => setShowCreate(true)} title="Créer une nouvelle campagne prospect" className="px-4 py-2 rounded-lg bg-[#FF007F] hover:bg-[#E50073] text-white text-sm font-semibold transition-all duration-150">+ Nouvelle campagne prospect</button>
      </div>

      {showCreate && (
        <div className="bg-[#1C1A17]/85 border border-warm rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-warm flex items-center justify-between bg-[#0E0D0B]">
            <div>
              <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">Nouvelle campagne prospect</h3>
              <p className="text-[#F5F4F1]/45 text-xs mt-0.5">Mêmes options qu'une vraie campagne agence.</p>
            </div>
            <button onClick={() => setShowCreate(false)} title="Fermer" className="p-1.5 rounded-md bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1]/55 hover:text-[#F5F4F1] transition-all duration-150">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-6 max-h-[75vh] overflow-y-auto">

            {/* ── Section : Informations de base ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Informations</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Nom de la campagne *</label>
                  <input value={newCamp.name} onChange={e => setNewCamp(p => ({...p, name: e.target.value}))}
                    placeholder="Ex: Campagne MrBeast Highlights"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Nom de l'agence cible *</label>
                  <input value={newCamp.agency_name} onChange={e => setNewCamp(p => ({...p, agency_name: e.target.value}))}
                    placeholder="Ex: Marcus Lawrence Agency"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">URL image de couverture (optionnel)</label>
                  <input value={newCamp.image_url} onChange={e => setNewCamp(p => ({...p, image_url: e.target.value}))}
                    placeholder="https://..."
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Description</label>
                  <textarea value={newCamp.description} onChange={e => setNewCamp(p => ({...p, description: e.target.value}))}
                    placeholder="Décris la campagne en quelques phrases..."
                    rows={2}
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22 resize-none" />
                </div>
              </div>
            </section>

            {/* ── Section : Modèle de paiement ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Modèle de paiement</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "views", label: "💰 Vues", desc: "RPM (€ / 1000 vues)" },
                  { id: "clicks", label: "🔗 Clics", desc: "€ / 1000 clics" },
                  { id: "both", label: "Vues + Clics", desc: "Cumul des deux" },
                ].map(m => (
                  <button key={m.id} onClick={() => setNewCamp(p => ({...p, payment_model: m.id}))}
                    className={`p-3 rounded-lg text-left transition-all duration-150 border ${newCamp.payment_model === m.id ? "bg-[#FF007F]/10 border-[#FF007F]/40 text-[#F5F4F1]" : "bg-[#0E0D0B] border-warm text-[#F5F4F1]/60 hover:border-[#F5F4F1]/14 hover:text-[#F5F4F1]"}`}>
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-[10px] text-[#F5F4F1]/45 mt-0.5">{m.desc}</p>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(newCamp.payment_model === "views" || newCamp.payment_model === "both") && (
                  <div>
                    <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">RPM (€ / 1000 vues) *</label>
                    <input type="number" step="0.1" value={newCamp.rpm} onChange={e => setNewCamp(p => ({...p, rpm: e.target.value}))}
                      placeholder="Ex: 5"
                      className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                  </div>
                )}
                {(newCamp.payment_model === "clicks" || newCamp.payment_model === "both") && (
                  <>
                    <div>
                      <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Tarif (€ / 1000 clics) *</label>
                      <input type="number" step="0.1" value={newCamp.rate_per_click} onChange={e => setNewCamp(p => ({...p, rate_per_click: e.target.value}))}
                        placeholder="Ex: 5"
                        className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">URL de destination *</label>
                      <input value={newCamp.destination_url} onChange={e => setNewCamp(p => ({...p, destination_url: e.target.value}))}
                        placeholder="https://..."
                        className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                    </div>
                    <div>
                      <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Comptage des clics</label>
                      <select value={newCamp.click_billing_mode} onChange={e => setNewCamp(p => ({...p, click_billing_mode: e.target.value}))}
                        className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm focus:outline-none focus:border-[#F5F4F1]/22">
                        <option value="all">Tous les clics</option>
                        <option value="unique_24h">Uniques / 24h (recommandé)</option>
                        <option value="unique_lifetime">Uniques à vie</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* ── Section : Plateformes ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Plateformes acceptées</p>
              <div className="flex gap-2">
                {[["tiktok", "🎵 TikTok"], ["instagram", "📸 Instagram"], ["youtube", "▶️ YouTube"]].map(([id, label]) => (
                  <button key={id} onClick={() => togglePlatform(id)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 border ${newCamp.platforms.includes(id) ? "bg-[#F5F4F1]/10 border-[#F5F4F1]/30 text-[#F5F4F1]" : "bg-[#0E0D0B] border-warm text-[#F5F4F1]/55 hover:border-[#F5F4F1]/14 hover:text-[#F5F4F1]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </section>

            {/* ── Section : Budget ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Budget</p>
              <label className="flex items-center gap-2 text-sm text-[#F5F4F1]/70 cursor-pointer">
                <input type="checkbox" checked={newCamp.budget_unlimited}
                  onChange={e => setNewCamp(p => ({...p, budget_unlimited: e.target.checked}))}
                  className="w-4 h-4 accent-[#FF007F]" />
                Budget illimité
              </label>
              {!newCamp.budget_unlimited && (
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Budget total (€)</label>
                  <input type="number" value={newCamp.budget_total} onChange={e => setNewCamp(p => ({...p, budget_total: e.target.value}))}
                    placeholder="Ex: 1000"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
              )}
            </section>

            {/* ── Section : Règles & Strikes ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Règles de la campagne</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Max clippeurs</label>
                  <input type="number" value={newCamp.max_clippers} onChange={e => setNewCamp(p => ({...p, max_clippers: e.target.value}))}
                    placeholder="Illimité"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Posts/jour min</label>
                  <input type="number" value={newCamp.cadence} onChange={e => setNewCamp(p => ({...p, cadence: e.target.value}))}
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Max strikes</label>
                  <input type="number" value={newCamp.max_strikes} onChange={e => setNewCamp(p => ({...p, max_strikes: e.target.value}))}
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Jours d'inact.</label>
                  <input type="number" value={newCamp.strike_days} onChange={e => setNewCamp(p => ({...p, strike_days: e.target.value}))}
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Vues min payées</label>
                  <input type="number" value={newCamp.min_view_payout} onChange={e => setNewCamp(p => ({...p, min_view_payout: e.target.value}))}
                    placeholder="0"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
                <div>
                  <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Vues max payées (cap)</label>
                  <input type="number" value={newCamp.max_view_payout} onChange={e => setNewCamp(p => ({...p, max_view_payout: e.target.value}))}
                    placeholder="Sans plafond"
                    className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                </div>
              </div>
            </section>

            {/* ── Section : Tracking ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Tracking</p>
              <div>
                <label className="text-xs text-[#F5F4F1]/55 block mb-1.5 font-medium">Date de début du tracking</label>
                <input type="date" value={newCamp.tracking_start_date} onChange={e => setNewCamp(p => ({...p, tracking_start_date: e.target.value}))}
                  className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2.5 text-[#F5F4F1] text-sm focus:outline-none focus:border-[#F5F4F1]/22" />
                <p className="text-[10px] text-[#F5F4F1]/40 mt-1.5 leading-relaxed">Toutes les vidéos publiées depuis cette date seront trackées + rémunérées. Vide = aujourd'hui.</p>
              </div>
            </section>

            {/* ── Section : Candidature ── */}
            <section className="space-y-3">
              <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-widest font-medium">Candidature</p>
              <label className="flex items-center gap-2 text-sm text-[#F5F4F1]/70 cursor-pointer">
                <input type="checkbox" checked={newCamp.application_form_enabled}
                  onChange={e => setNewCamp(p => ({...p, application_form_enabled: e.target.checked}))}
                  className="w-4 h-4 accent-[#FF007F]" />
                Activer le formulaire de candidature
              </label>
              {newCamp.application_form_enabled && (
                <div className="space-y-2">
                  <p className="text-xs text-[#F5F4F1]/55">Questions à poser aux candidats :</p>
                  {(newCamp.application_questions || []).map((q, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input value={q}
                        onChange={e => setNewCamp(prev => ({ ...prev, application_questions: prev.application_questions.map((qq, i) => i === idx ? e.target.value : qq) }))}
                        placeholder="Question…"
                        className="flex-1 bg-[#0E0D0B] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                      {newCamp.application_questions.length > 1 && (
                        <button onClick={() => setNewCamp(prev => ({ ...prev, application_questions: prev.application_questions.filter((_, i) => i !== idx) }))}
                          title="Supprimer cette question"
                          className="w-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 transition-all duration-150">
                          <X className="w-3.5 h-3.5 mx-auto" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setNewCamp(prev => ({ ...prev, application_questions: [...(prev.application_questions || []), ""] }))}
                    className="w-full py-2 rounded-lg bg-[#0E0D0B] hover:bg-[#262320] text-[#F5F4F1]/55 hover:text-[#F5F4F1] text-xs border border-dashed border-warm transition-all duration-150">
                    + Ajouter une question
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Footer fixe */}
          <div className="px-5 py-4 border-t border-warm flex gap-2 bg-[#0E0D0B]">
            <button onClick={() => setShowCreate(false)}
              className="px-5 py-2.5 rounded-lg border border-warm bg-[#1C1A17] hover:bg-[#262320] text-[#F5F4F1]/70 hover:text-[#F5F4F1] text-sm transition-all duration-150">Annuler</button>
            <button onClick={createCampaign}
              className="flex-1 py-2.5 rounded-lg bg-[#FF007F] hover:bg-[#E50073] text-white text-sm font-semibold transition-all duration-150">
              Créer la campagne prospect
            </button>
          </div>
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" /></div>}
      {!loading && prospects.length === 0 && (
        <div className="text-center py-16 bg-[#1C1A17]/85 border border-warm rounded-xl">
          <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mx-auto mb-3">
            <Building2 className="w-4 h-4 text-[#F5F4F1]/30" />
          </div>
          <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun prospect créé</p>
          <p className="text-[#F5F4F1]/35 text-xs mt-1">Crée une campagne prospect pour démarcher une agence.</p>
        </div>
      )}

      <div className="space-y-3">
        {prospects.map(p => {
          const agencyLink = `${baseUrl}/claim/agency/${p.prospect_agency_token}`;
          const clipperLink = `${baseUrl}/claim/clipper/${p.prospect_clipper_token}`;
          return (
            <div key={p.campaign_id} className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-[#F5F4F1] font-display font-semibold tracking-tight">{p.name}</p>
                  <p className="text-[#F5F4F1]/45 text-xs mt-0.5">Agence cible <span className="text-[#F5F4F1]/70 ml-1">{p.agency_name}</span> <span className="mx-1.5 text-[#F5F4F1]/25">·</span> {p.payment_model === "clicks" ? `${p.rate_per_click}€/1K clics` : `${p.rpm}€/1K vues`}</p>
                </div>
                <div className="flex gap-3 text-xs flex-shrink-0 tabular-nums">
                  <span className="text-[#F5F4F1]/65">{p.prospect_accounts_count} comptes</span>
                  <span className="text-[#00E5FF]">{p.tracked_videos_count} vidéos</span>
                  <span className="text-[#39FF14]">{(p.total_views || 0).toLocaleString("fr-FR")} vues</span>
                </div>
              </div>

              {/* Liens magiques + Apercu */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2 flex items-center gap-2">
                  <span className="text-[10px] text-[#F5F4F1]/55 uppercase font-semibold tracking-wider w-14">Agence</span>
                  <code className="flex-1 text-xs text-[#F5F4F1]/70 truncate">{agencyLink}</code>
                  <button onClick={() => copyToClipboard(agencyLink)} title="Copier le lien" className="text-[#00E5FF] hover:bg-[#00E5FF]/20 text-xs px-2 py-1 rounded-md bg-[#00E5FF]/10 border border-[#00E5FF]/25 transition-all duration-150">Copier</button>
                </div>
                <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2 flex items-center gap-2">
                  <span className="text-[10px] text-[#F5F4F1]/55 uppercase font-semibold tracking-wider w-14">Clipper</span>
                  <code className="flex-1 text-xs text-[#F5F4F1]/70 truncate">{clipperLink}</code>
                  <button onClick={() => copyToClipboard(clipperLink)} title="Copier le lien" className="text-[#FF007F] hover:bg-[#FF007F]/20 text-xs px-2 py-1 rounded-md bg-[#FF007F]/10 border border-[#FF007F]/25 transition-all duration-150">Copier</button>
                </div>
              </div>

              {/* Bouton Apercu agence */}
              <button
                onClick={async () => {
                  if (!p.agency_id) { toast.error("Pas d'agence ghost"); return; }
                  try {
                    const res = await fetch(`${API}/admin/preview-as/${p.agency_id}`, {
                      method: "POST", credentials: "include",
                      headers: { "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
                    });
                    if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Erreur"); return; }
                    const d = await res.json();
                    window.open(`/agency/campaign/${p.campaign_id}`, "_blank");
                    toast.success("Aperçu (2 h) — relog admin requis après");
                  } catch (e) { toast.error(e.message); }
                }}
                title="Ouvrir la campagne comme la verra l'agence"
                className="w-full py-2 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] text-xs font-medium border border-[#00E5FF]/25 transition-all duration-150 flex items-center justify-center gap-2">
                <Eye className="w-3.5 h-3.5" />
                Aperçu — voir comme l'agence verra cette campagne
              </button>

              {/* Bouton ajouter clippeur */}
              {/* Liste des comptes deja pre-enregistres */}
              {accountsMap[p.campaign_id]?.total > 0 && (
                <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 space-y-2">
                  <p className="text-[11px] text-[#F5F4F1]/55 font-medium uppercase tracking-wider">Clippeurs pré-enregistrés ({accountsMap[p.campaign_id].total} comptes)</p>
                  <div className="space-y-1.5">
                    {Object.entries(accountsMap[p.campaign_id].by_discord || {}).map(([discord, accs]) => (
                      <div key={discord} className="flex items-center gap-2 flex-wrap py-1 px-2 bg-[#1C1A17] border border-warm rounded-md">
                        <span className="text-xs text-[#39FF14] font-medium">@{discord}</span>
                        <span className="text-[#F5F4F1]/30 text-xs">→</span>
                        {accs.map(a => (
                          <span key={a.account_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#0E0D0B] border border-warm text-[11px]">
                            <span className="text-[#F5F4F1]/45">{a.platform === "tiktok" ? "🎵" : a.platform === "instagram" ? "📸" : "▶️"}</span>
                            <span className="text-[#F5F4F1]/80">@{a.username}</span>
                            <button onClick={() => deleteProspectAccount(a.account_id, p.campaign_id)}
                              title="Supprimer ce compte"
                              className="text-red-400/50 hover:text-red-400 ml-1">✕</button>
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {addingClipper === p.campaign_id ? (
                <div className="bg-[#0E0D0B] border border-[#39FF14]/30 rounded-lg p-3 space-y-2">
                  <p className="text-xs text-[#F5F4F1]/60">Saisis le pseudo Discord puis ajoute autant de comptes sociaux que voulu pour ce clippeur :</p>
                  <input value={bulkClipper.discord_username} onChange={e => setBulkClipper(prev => ({...prev, discord_username: e.target.value}))}
                    placeholder="Pseudo Discord du clippeur (ex: paul_clipper)"
                    className="w-full bg-[#1C1A17] border border-[#39FF14]/30 rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#39FF14]/50" />
                  <div className="space-y-1.5">
                    {bulkClipper.accounts.map((acc, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <select value={acc.platform}
                          onChange={e => setBulkClipper(prev => ({ ...prev, accounts: prev.accounts.map((a, i) => i === idx ? { ...a, platform: e.target.value } : a) }))}
                          className="bg-[#1C1A17] border border-warm rounded-lg px-2 py-2 text-[#F5F4F1] text-sm w-32 focus:outline-none focus:border-[#F5F4F1]/22">
                          <option value="tiktok">🎵 TikTok</option>
                          <option value="instagram">📸 Instagram</option>
                          <option value="youtube">▶️ YouTube</option>
                        </select>
                        <input value={acc.username}
                          onChange={e => setBulkClipper(prev => ({ ...prev, accounts: prev.accounts.map((a, i) => i === idx ? { ...a, username: e.target.value } : a) }))}
                          placeholder="username (sans @)"
                          className="flex-1 bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22" />
                        {bulkClipper.accounts.length > 1 && (
                          <button onClick={() => setBulkClipper(prev => ({ ...prev, accounts: prev.accounts.filter((_, i) => i !== idx) }))}
                            title="Retirer ce compte"
                            className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 transition-all duration-150">
                            <X className="w-3.5 h-3.5 mx-auto" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setBulkClipper(prev => ({ ...prev, accounts: [...prev.accounts, { platform: prev.accounts[prev.accounts.length-1]?.platform === "tiktok" ? "instagram" : "tiktok", username: "" }] }))}
                      className="w-full py-1.5 rounded-lg bg-[#1C1A17] hover:bg-[#262320] text-[#F5F4F1]/55 hover:text-[#F5F4F1] text-xs transition-all duration-150 border border-dashed border-warm">
                      + Ajouter un autre compte pour ce clippeur
                    </button>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => submitBulkClipper(p.campaign_id)} className="flex-1 py-2 rounded-lg bg-[#39FF14]/15 hover:bg-[#39FF14]/25 text-[#39FF14] text-sm font-medium border border-[#39FF14]/30 transition-all duration-150">
                      Ajouter tous les comptes
                    </button>
                    <button onClick={() => { setAddingClipper(null); setBulkClipper({ discord_username: "", accounts: [{ platform: "tiktok", username: "" }] }); }} className="px-4 py-2 rounded-lg border border-warm bg-[#1C1A17] hover:bg-[#262320] text-[#F5F4F1]/65 hover:text-[#F5F4F1] text-sm transition-all duration-150">Fermer</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAddingClipper(p.campaign_id)} className="w-full py-1.5 rounded-lg bg-[#0E0D0B] hover:bg-[#262320] border border-warm text-[#F5F4F1]/60 hover:text-[#F5F4F1] text-xs transition-all duration-150">+ Ajouter un clippeur (Discord + ses comptes)</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Capacite & Couts ─────────────────────────────────────────────
function ApifyAlarmBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const refresh = () => adminFetch("/admin/apify-usage-today").then(setData).catch(() => {});
    refresh();
    const i = setInterval(refresh, 2 * 60 * 1000);
    return () => clearInterval(i);
  }, []);

  if (!data || data.error || (data.apify_total_today || 0) === 0) return null;

  // Pas d'estimation : juste les compteurs reels
  return (
    <div className="rounded-xl border p-4 bg-[#FFB300]/10 border-[#FFB300]/30">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-[#FFB300]/10 border border-[#FFB300]/30 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4.5 h-4.5 text-[#FFB300]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#FFB300] font-display font-semibold text-sm tracking-tight">Apify utilisé aujourd'hui</p>
          <p className="text-[#F5F4F1]/70 text-xs mt-1 leading-relaxed">
            Apify ne devrait être utilisé qu'en dernier recours. Si le compteur monte, le scraping gratuit est à investiguer.
          </p>
          <p className="text-[#F5F4F1]/55 text-[11px] mt-1.5 tabular-nums">
            Aujourd'hui <span className="font-mono text-[#F5F4F1] mx-1">{data.apify_total_today || 0}</span> calls Apify
            (Insta {data.apify_instagram_today || 0} + TikTok {data.apify_tiktok_today || 0})
            sur <span className="font-mono text-[#F5F4F1] mx-1">{data.total_scrapes_today || 0}</span> scrapes total.
          </p>
        </div>
      </div>
    </div>
  );
}

function MetaBusinessTestSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const d = await adminFetch("/admin/test-meta-business-discovery", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = (verdict) =>
    verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : verdict?.startsWith("🟡") ? "text-amber-400 border-amber-500/40 bg-amber-500/5"
    : verdict?.startsWith("🟠") ? "text-orange-400 border-orange-500/40 bg-orange-500/5"
    : "text-red-400 border-red-500/40 bg-red-500/5";

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🌟 Meta Business Discovery — Test des comptes Insta
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Vérifie quels comptes Insta sont Business/Creator (= 99% fiabilité) vs Personnel (= tombe sur fallback).
          </p>
        </div>
        <button
          onClick={runTest}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 border border-purple-500/40 text-sm font-bold transition-all disabled:opacity-50"
        >
          {loading ? <>⏳ Test (peut prendre 1-3 min)…</> : "🌟 Tester tous les comptes"}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 text-red-400 text-xs">{error}</div>}

      {data && (
        <>
          <div className={`border rounded-lg p-3 text-sm font-bold ${verdictColor(data.verdict)}`}>
            {data.verdict}
          </div>

          {/* Config Meta */}
          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 text-xs grid grid-cols-2 gap-2">
            <p className="text-[#F5F4F1]/60">IG_BUSINESS_ACCOUNT_ID <span className={`ml-1 ${data.config?.ig_business_account_id_set ? "text-[#39FF14]" : "text-red-400"}`}>{data.config?.ig_business_account_id_set ? "Set" : "Manquant"}</span></p>
            <p className="text-[#F5F4F1]/60">IG_LONG_LIVED_TOKEN <span className={`ml-1 ${data.config?.ig_long_lived_token_set ? "text-[#39FF14]" : "text-red-400"}`}>{data.config?.ig_long_lived_token_set ? "Set" : "Manquant"}</span></p>
            {data.token_valid !== undefined && (
              <p className="text-[#F5F4F1]/60 col-span-2">Token valide <span className={`ml-1 ${data.token_valid ? "text-[#39FF14]" : "text-red-400"}`}>{data.token_valid ? "OUI" : "EXPIRÉ / INVALIDE"}</span>
              {data.business_account_username && <span className="text-[#F5F4F1]/45 ml-2">(@{data.business_account_username})</span>}</p>
            )}
          </div>

          {/* Stats */}
          {data.account_count !== undefined && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2.5 text-center">
                  <p className="text-[#F5F4F1]/55 text-[10px] uppercase tracking-wider">Total</p>
                  <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-lg">{data.tested_count}</p>
                </div>
                <div className="bg-[#39FF14]/10 border border-[#39FF14]/25 rounded-lg p-2.5 text-center">
                  <p className="text-[#39FF14]/80 text-[10px] uppercase tracking-wider">Business</p>
                  <p className="text-[#39FF14] font-display font-semibold tabular-nums text-lg">{data.business_ready_count}</p>
                </div>
                <div className="bg-[#FFB300]/10 border border-[#FFB300]/25 rounded-lg p-2.5 text-center">
                  <p className="text-[#FFB300]/80 text-[10px] uppercase tracking-wider">Personnel</p>
                  <p className="text-[#FFB300] font-display font-semibold tabular-nums text-lg">{data.not_business_count}</p>
                </div>
                <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-lg p-2.5 text-center">
                  <p className="text-[#00E5FF]/80 text-[10px] uppercase tracking-wider">% Business</p>
                  <p className="text-[#00E5FF] font-display font-semibold tabular-nums text-lg">{data.business_ready_pct}%</p>
                </div>
              </div>

              {/* Action à faire */}
              {data.action_to_take && (
                <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 space-y-1.5">
                  <p className="text-[#F5F4F1]/60 text-xs font-display font-semibold tracking-tight">Message à envoyer aux clippeurs perso</p>
                  <p className="text-[#F5F4F1] text-[11px] leading-relaxed italic">{data.action_to_take}</p>
                </div>
              )}

              {/* Listes */}
              <div className="grid grid-cols-2 gap-3">
                <details className="bg-[#39FF14]/8 border border-[#39FF14]/20 rounded-lg p-2.5">
                  <summary className="text-[#39FF14] text-xs cursor-pointer font-display font-semibold tracking-tight">Business/Creator ({data.business_ready_count})</summary>
                  <div className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
                    {(data.business_ready_accounts || []).map(u => (
                      <p key={u} className="text-[#F5F4F1]/65 text-[10px] font-mono">@{u}</p>
                    ))}
                  </div>
                </details>
                <details className="bg-[#FFB300]/8 border border-[#FFB300]/20 rounded-lg p-2.5">
                  <summary className="text-[#FFB300] text-xs cursor-pointer font-display font-semibold tracking-tight">Comptes perso ({data.not_business_count})</summary>
                  <div className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
                    {(data.not_business_accounts || []).map(u => (
                      <p key={u} className="text-[#F5F4F1]/65 text-[10px] font-mono">@{u}</p>
                    ))}
                  </div>
                </details>
              </div>
            </>
          )}
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-4 text-[#F5F4F1]/40 text-xs">
          Clique pour tester Meta Business Discovery sur tous tes comptes Insta.
          <br />
          Résultat : quels comptes sont à 99% fiabilité vs ceux à demander de switcher en Creator.
        </div>
      )}
    </div>
  );
}

function MultiPlatformStressTestSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runStressTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      // 40 iterations sur 3 plateformes = ~10-15 min
      const d = await adminFetch("/admin/stress-test-all-platforms?n_iterations=40", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = (verdict) =>
    verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : verdict?.startsWith("🟡") ? "text-amber-400 border-amber-500/40 bg-amber-500/5"
    : verdict?.startsWith("🟠") ? "text-orange-400 border-orange-500/40 bg-orange-500/5"
    : "text-red-400 border-red-500/40 bg-red-500/5";

  const renderPlatformPanel = (platformData, platformName, icon, color) => {
    if (!platformData) return null;
    return (
      <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{icon}</span>
          <h4 className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">{platformName}</h4>
        </div>
        <div className={`border rounded-md p-2 text-xs font-display font-semibold tracking-tight ${verdictColor(platformData.verdict)}`}>
          {platformData.verdict}
        </div>
        <div className="grid grid-cols-4 gap-1.5 text-center">
          <div className="bg-[#1C1A17] border border-warm rounded-md p-1.5">
            <p className="text-[#F5F4F1]/45 text-[9px] uppercase tracking-wider">Total</p>
            <p className="text-[#F5F4F1] font-mono font-semibold tabular-nums text-sm">{platformData.n_iterations}</p>
          </div>
          <div className="bg-[#39FF14]/10 border border-[#39FF14]/25 rounded-md p-1.5">
            <p className="text-[#39FF14]/70 text-[9px] uppercase tracking-wider">OK</p>
            <p className="text-[#39FF14] font-mono font-semibold tabular-nums text-sm">{platformData.success_count}</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/25 rounded-md p-1.5">
            <p className="text-red-400/70 text-[9px] uppercase tracking-wider">KO</p>
            <p className="text-red-400 font-mono font-semibold tabular-nums text-sm">{platformData.fail_count}</p>
          </div>
          <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-md p-1.5">
            <p className="text-[#00E5FF]/70 text-[9px] uppercase tracking-wider">%</p>
            <p className="text-[#00E5FF] font-mono font-semibold tabular-nums text-sm">{platformData.success_rate_pct}%</p>
          </div>
        </div>
        <div className="text-[10px] space-y-0.5">
          {Object.entries(platformData.by_account || {}).map(([acc, s]) => (
            <div key={acc} className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${s.success_rate >= 80 ? "bg-[#39FF14]" : s.success_rate >= 50 ? "bg-[#FFB300]" : "bg-red-400"}`} />
              <span className="text-[#F5F4F1]/65 font-mono truncate">{acc}</span>
              <span className={`tabular-nums ${s.success_rate >= 80 ? "text-[#39FF14]" : "text-red-400"}`}>{s.success_rate}%</span>
            </div>
          ))}
        </div>
        <details className="bg-[#1C1A17] border border-warm rounded-md p-1.5">
          <summary className="text-[#F5F4F1]/55 text-[10px] cursor-pointer">{platformData.n_iterations} itérations</summary>
          <div className="mt-1 max-h-40 overflow-y-auto space-y-0.5">
            {platformData.results.map((r) => (
              <div key={r.iteration} className="flex gap-1.5 text-[9px] font-mono">
                <span className="text-[#F5F4F1]/40 w-6 tabular-nums">#{r.iteration}</span>
                <span className={r.ok ? "text-[#39FF14]" : "text-red-400"}>{r.ok ? "OK" : "KO"}</span>
                <span className="text-[#F5F4F1]/65 truncate flex-1">@{r.username} <span className="text-[#F5F4F1]/30">·</span> {r.video_count || 0} vid <span className="text-[#F5F4F1]/30">·</span> {r.duration_ms}ms</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🔥 STRESS TEST 3 PLATEFORMES (40 scrapes chacune)
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Lance 40 scrapes consécutifs sur Instagram + TikTok + YouTube (120 tests total). Mesure la fiabilité réelle. Durée : 5-15 min.
          </p>
        </div>
        <button
          onClick={runStressTest}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 border border-purple-500/40 text-sm font-bold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {loading
            ? <><div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" /> Stress test… (5-15 min)</>
            : "🔥 Lancer 120 tests (40×3)"}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 text-red-400 text-xs">{error}</div>}

      {data && (
        <>
          <div className={`border rounded-lg p-3 text-sm font-bold ${verdictColor(data.global_verdict)}`}>
            {data.global_verdict}
          </div>

          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 grid grid-cols-2 gap-2 text-[11px]">
            <p className="text-[#F5F4F1]/60">Moyenne <span className="text-[#F5F4F1] font-mono font-semibold tabular-nums ml-1">{data.avg_success_rate_pct}%</span></p>
            <p className="text-[#F5F4F1]/60">Pire plateforme <span className="text-[#F5F4F1] font-mono font-semibold tabular-nums ml-1">{data.min_success_rate_pct}%</span></p>
            <p className="text-[#F5F4F1]/60">Durée totale <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.total_duration_sec}s</span></p>
            <p className="text-[#F5F4F1]/60">Iterations/plateforme <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.n_iterations_per_platform}</span></p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {renderPlatformPanel(data.instagram, "Instagram", "📸", "purple")}
            {renderPlatformPanel(data.tiktok, "TikTok", "🎵", "cyan")}
            {renderPlatformPanel(data.youtube, "YouTube", "▶️", "red")}
          </div>

          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 text-[11px] grid grid-cols-3 gap-2">
            <p className="text-[#F5F4F1]/60">Proxies <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.config.proxy_pool_size}</span></p>
            <p className="text-[#F5F4F1]/60">Cookies Insta <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.config.instagram_sessions_count}</span></p>
            <p className="text-[#F5F4F1]/60">VPS <span className={`ml-1 ${data.config.vps_configured ? "text-[#39FF14]" : "text-red-400"}`}>{data.config.vps_configured ? "OK" : "KO"}</span></p>
            <p className="text-[#F5F4F1]/60">YT API <span className={`ml-1 ${data.config.youtube_api_configured ? "text-[#39FF14]" : "text-red-400"}`}>{data.config.youtube_api_configured ? "OK" : "KO"}</span></p>
            <p className="text-[#F5F4F1]/60">TikWm <span className={`ml-1 ${data.config.tikwm_api_configured ? "text-[#39FF14]" : "text-red-400"}`}>{data.config.tikwm_api_configured ? "OK" : "KO"}</span></p>
            <p className="text-[#F5F4F1]/60">Meta Business <span className={`ml-1 ${data.config.ig_business_configured ? "text-[#39FF14]" : "text-[#FFB300]"}`}>{data.config.ig_business_configured ? "OK" : "KO"}</span></p>
          </div>
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-4 text-[#F5F4F1]/40 text-xs">
          Clique pour lancer 40 scrapes consécutifs sur chaque plateforme.
          <br />
          Résultat : taux de fiabilité réel + recommandations.
        </div>
      )}
    </div>
  );
}

function InstaStressTestSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runStressTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const d = await adminFetch("/admin/insta-stress-test-30?n_iterations=30", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = data?.verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : data?.verdict?.startsWith("🟡") ? "text-amber-400 border-amber-500/40 bg-amber-500/5"
    : data?.verdict?.startsWith("🟠") ? "text-orange-400 border-orange-500/40 bg-orange-500/5"
    : "text-red-400 border-red-500/40 bg-red-500/5";

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🔥 Stress Test Instagram (30 scrapes consécutifs)
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Lance 30 scrapes Insta sur 5 comptes Business publics différents. Donne un taux de succès réel sur production.
          </p>
        </div>
        <button
          onClick={runStressTest}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 border border-purple-500/40 text-sm font-bold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {loading
            ? <><div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" /> Stress test… (peut prendre 2-5 min)</>
            : "🔥 Lancer stress test 30x"}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 text-red-400 text-xs">{error}</div>}

      {data && (
        <>
          <div className={`border rounded-lg p-3 text-sm font-bold ${verdictColor}`}>
            {data.verdict}
          </div>

          <div className="grid grid-cols-5 gap-2">
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2.5 text-center">
              <p className="text-[#F5F4F1]/55 text-[10px] uppercase tracking-wider">Total</p>
              <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-lg">{data.n_iterations}</p>
            </div>
            <div className="bg-[#39FF14]/10 border border-[#39FF14]/25 rounded-lg p-2.5 text-center">
              <p className="text-[#39FF14]/80 text-[10px] uppercase tracking-wider">Succès</p>
              <p className="text-[#39FF14] font-display font-semibold tabular-nums text-lg">{data.success_count}</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 text-center">
              <p className="text-red-400/80 text-[10px] uppercase tracking-wider">Échecs</p>
              <p className="text-red-400 font-display font-semibold tabular-nums text-lg">{data.fail_count}</p>
            </div>
            <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-lg p-2.5 text-center">
              <p className="text-[#00E5FF]/80 text-[10px] uppercase tracking-wider">Taux</p>
              <p className="text-[#00E5FF] font-display font-semibold tabular-nums text-lg">{data.success_rate_pct}%</p>
            </div>
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2.5 text-center">
              <p className="text-[#F5F4F1]/55 text-[10px] uppercase tracking-wider">Durée</p>
              <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-lg">{data.total_duration_sec}s</p>
            </div>
          </div>

          {/* Par compte */}
          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
            <p className="text-[#F5F4F1] text-xs font-display font-semibold tracking-tight mb-2">Détail par compte</p>
            <div className="space-y-1.5">
              {Object.entries(data.by_account || {}).map(([acc, stats]) => (
                <div key={acc} className="flex items-center gap-2 text-[11px]">
                  <span className={`w-2 h-2 rounded-full ${stats.success_rate >= 80 ? "bg-[#39FF14]" : stats.success_rate >= 50 ? "bg-[#FFB300]" : "bg-red-400"}`} />
                  <span className="text-[#F5F4F1]/80 font-mono w-32 truncate">@{acc}</span>
                  <span className="text-[#F5F4F1]/55 w-16 tabular-nums">{stats.success_rate}%</span>
                  <span className="text-[#F5F4F1]/45 tabular-nums">{stats.ok}/{stats.tries} OK · {stats.avg_videos} vidéos/réussite</span>
                </div>
              ))}
            </div>
          </div>

          {/* Config détectée */}
          <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 text-[11px] grid grid-cols-2 gap-2">
            <p className="text-[#F5F4F1]/60">Proxies <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.config.proxy_pool_size}</span></p>
            <p className="text-[#F5F4F1]/60">Sémaphore <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.config.insta_sem_size} parallèles · {data.config.insta_delay_sec}s</span></p>
            <p className="text-[#F5F4F1]/60">Cookies Insta <span className="text-[#F5F4F1] font-mono tabular-nums ml-1">{data.config.instagram_sessions_count}</span></p>
            <p className="text-[#F5F4F1]/60">Meta Business <span className={`ml-1 ${data.config.ig_business_configured ? "text-[#39FF14]" : "text-[#FFB300]"}`}>{data.config.ig_business_configured ? "configuré" : "pas configuré"}</span></p>
          </div>

          {/* Détail itérations (collapse) */}
          <details className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
            <summary className="text-[#F5F4F1]/60 text-xs cursor-pointer">Détail des {data.n_iterations} itérations</summary>
            <div className="mt-2 max-h-64 overflow-y-auto space-y-0.5">
              {data.results.map((r) => (
                <div key={r.iteration} className="flex items-center gap-2 text-[10px] font-mono">
                  <span className="text-[#F5F4F1]/35 w-8 tabular-nums">#{r.iteration}</span>
                  <span className="text-[#F5F4F1]/60 w-24 truncate">@{r.username}</span>
                  <span className={r.ok ? "text-[#39FF14] w-8" : "text-red-400 w-8"}>{r.ok ? "OK" : "KO"}</span>
                  <span className="text-[#F5F4F1]/45 w-24 tabular-nums">{r.video_count || 0} vidéos</span>
                  <span className="text-[#F5F4F1]/45 tabular-nums">{r.duration_ms}ms</span>
                  {r.error && <span className="text-red-400/70 truncate flex-1">{r.error.slice(0, 50)}</span>}
                </div>
              ))}
            </div>
          </details>
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-4 text-[#F5F4F1]/40 text-xs">
          Clique sur "Lancer stress test 30x" pour mesurer la fiabilité réelle d'Instagram.
          <br />
          Si taux ≥ 90% → production-ready. Sinon je te dis quoi ajouter.
        </div>
      )}
    </div>
  );
}

function ProxyVpsDeepTestSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runTest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // adminFetch retourne deja le JSON parse (pas un Response)
      const d = await adminFetch("/admin/test-proxy-vps-deep", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = data?.verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : data?.verdict?.startsWith("🔴") ? "text-red-400 border-red-500/40 bg-red-500/5"
    : "text-amber-400 border-amber-500/40 bg-amber-500/5";

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🔬 Test proxy Webshare + VPS Hostinger — DIAGNOSTIC ROOT CAUSE
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Test chaque composant en isolation : IP cloud, IP via proxy, VPS /health, VPS scrape, Insta via proxy, TikWm via proxy.
          </p>
        </div>
        <button
          onClick={runTest}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 border border-orange-500/40 text-sm font-bold transition-all disabled:opacity-50"
        >
          {loading ? <>⏳ Test…</> : "🔬 Test approfondi"}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 text-red-400 text-xs">{error}</div>}

      {data && (
        <>
          <div className={`border rounded-lg p-3 text-sm font-bold ${verdictColor}`}>{data.verdict}</div>

          {data.actions && data.actions.length > 0 && (
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 space-y-1.5">
              <p className="text-[#F5F4F1]/65 text-xs font-display font-semibold tracking-tight">Actions à faire</p>
              {data.actions.map((a, i) => (
                <p key={i} className="text-[#F5F4F1] text-xs leading-relaxed">{a}</p>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            {Object.entries(data.tests || {}).map(([name, t]) => (
              <div key={name} className={`p-2.5 rounded-lg border flex items-center gap-3 ${t.ok ? "bg-[#39FF14]/8 border-[#39FF14]/20" : "bg-red-500/8 border-red-500/25"}`}>
                <span className={`w-2 h-2 rounded-full ${t.ok ? "bg-[#39FF14]" : "bg-red-400"} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-display font-semibold tracking-tight ${t.ok ? "text-[#39FF14]" : "text-red-400"}`}>{name}</p>
                  <p className="text-[#F5F4F1]/55 text-[11px] truncate">{t.info || t.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-4 text-[#F5F4F1]/40 text-xs">Lance le test approfondi pour identifier la cause exacte du blocage.</div>
      )}
    </div>
  );
}

function MegaDiagnosticSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runDiag = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // adminFetch retourne deja le JSON parse
      const d = await adminFetch("/admin/mega-diagnostic", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = data?.verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : data?.verdict?.startsWith("🔴") ? "text-red-400 border-red-500/40 bg-red-500/5"
    : "text-amber-400 border-amber-500/40 bg-amber-500/5";

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🚨 MEGA DIAGNOSTIC — pourquoi le scraping marche pas ?
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Test TOUT en 30 sec : env vars + sources + scrape réel + analyse historique. Donne actions concrètes.
          </p>
        </div>
        <button
          onClick={runDiag}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/40 text-sm font-bold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {loading
            ? <><div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" /> Diagnostic en cours…</>
            : "🔍 Lancer le mega-diagnostic"}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-xs">❌ {error}</div>
      )}

      {data && (
        <>
          {/* Verdict global */}
          <div className={`border rounded-lg p-3 text-sm font-bold ${verdictColor}`}>
            {data.verdict}
          </div>

          {/* Actions à faire */}
          {data.actions_a_faire && data.actions_a_faire.length > 0 && (
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3 space-y-1.5">
              <p className="text-[#F5F4F1]/65 text-xs font-display font-semibold tracking-tight">Actions à faire</p>
              {data.actions_a_faire.map((a, i) => (
                <p key={i} className="text-white text-xs leading-relaxed">{a}</p>
              ))}
            </div>
          )}

          {/* Sources test */}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(data.sources || {}).map(([name, src]) => (
              <div key={name} className={`p-2.5 rounded-lg border ${src.ok ? "bg-[#39FF14]/8 border-[#39FF14]/20" : "bg-red-500/8 border-red-500/25"}`}>
                <p className={`text-xs font-display font-semibold tracking-tight ${src.ok ? "text-[#39FF14]" : "text-red-400"}`}>
                  {src.ok ? "OK" : "KO"} <span className="ml-1">{name}</span>
                </p>
                <p className="text-[#F5F4F1]/55 text-[10px] truncate mt-0.5">{src.info || src.reason}</p>
              </div>
            ))}
          </div>

          {/* Live scrape test */}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(data.live_scrape_test || {}).map(([platform, res]) => (
              <div key={platform} className={`p-2.5 rounded-lg border ${res.ok ? "bg-[#39FF14]/8 border-[#39FF14]/20" : "bg-red-500/8 border-red-500/25"}`}>
                <p className={`text-xs font-display font-semibold tracking-tight ${res.ok ? "text-[#39FF14]" : "text-red-400"}`}>
                  {res.ok ? "OK" : "KO"} Test {platform} {res.account_tested}
                </p>
                <p className="text-[#F5F4F1]/55 text-[10px] mt-0.5">
                  {res.ok ? `${res.videos_found} vidéos trouvées` : (res.error || "échec")}
                </p>
              </div>
            ))}
          </div>

          {/* Env vars */}
          <details className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
            <summary className="text-[#F5F4F1]/65 text-xs cursor-pointer">Env vars Railway</summary>
            <div className="mt-2 space-y-1">
              {Object.entries(data.env_vars || {}).map(([name, info]) => (
                <div key={name} className="flex items-center gap-2 text-[11px]">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${info.configured ? "bg-[#39FF14]" : "bg-red-400"}`} />
                  <span className="text-[#F5F4F1]/70 font-mono w-48 truncate">{name}</span>
                  <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-medium ${info.criticite === "CRITIQUE" ? "bg-red-500/15 text-red-400 border border-red-500/25" : info.criticite === "IMPORTANT" ? "bg-[#FFB300]/15 text-[#FFB300] border border-[#FFB300]/25" : "bg-[#1C1A17] text-[#F5F4F1]/55 border border-warm"}`}>
                    {info.criticite}
                  </span>
                  <span className="text-[#F5F4F1]/45 truncate flex-1">{info.note}</span>
                </div>
              ))}
            </div>
          </details>

          {/* Analyse 50 derniers scrapes */}
          {data.recent_scrapes_analysis?.by_source && (
            <details className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
              <summary className="text-[#F5F4F1]/65 text-xs cursor-pointer">
                Analyse {data.recent_scrapes_analysis.total_recent} derniers scrapes
              </summary>
              <div className="mt-2 space-y-1">
                {Object.entries(data.recent_scrapes_analysis.by_source).map(([src, stats]) => (
                  <div key={src} className="flex items-center gap-2 text-[11px]">
                    <span className="text-[#F5F4F1]/70 font-mono w-40 truncate">{src}</span>
                    <span className="text-[#39FF14] tabular-nums">{stats.ok} OK</span>
                    <span className="text-red-400 tabular-nums">{stats.ko} KO</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          <p className="text-[10px] text-[#F5F4F1]/30 italic tabular-nums">
            Testé le {new Date(data.tested_at).toLocaleString("fr-FR")}
          </p>
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-6 text-[#F5F4F1]/40 text-sm">
          Lance le mega-diagnostic pour identifier exactement pourquoi le scraping ne fonctionne pas.
        </div>
      )}
    </div>
  );
}

function ScrapingHealthCheckSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // adminFetch retourne deja le JSON parse (pas un Response)
      const d = await adminFetch("/admin/scraping-health-check", { method: "GET" });
      setData(d);
    } catch (e) {
      setError(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictColor = data?.verdict?.startsWith("✅") ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : data?.verdict?.startsWith("🔴") ? "text-red-400 border-red-500/40 bg-red-500/5"
    : "text-amber-400 border-amber-500/40 bg-amber-500/5";

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight flex items-center gap-2">
            🏥 Scraping Health Check
          </h3>
          <p className="text-[#F5F4F1]/45 text-xs mt-1 leading-relaxed">
            Test toutes les sources (VPS, proxy, Meta API, YouTube API, TikWm, RapidAPI, sessions Insta) en parallèle
          </p>
        </div>
        <button
          onClick={runCheck}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-[#00E5FF]/15 hover:bg-[#00E5FF]/25 text-[#00E5FF] border border-[#00E5FF]/40 text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {loading
            ? <><div className="w-3 h-3 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" /> Test en cours…</>
            : "▶ Lancer le test"}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-xs">
          ❌ {error}
        </div>
      )}

      {data && (
        <>
          {/* Verdict global */}
          <div className={`border rounded-lg p-3 text-sm font-semibold ${verdictColor}`}>
            {data.verdict}
          </div>

          {/* Summary counters */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2.5 text-center">
              <p className="text-[#F5F4F1]/55 text-[10px] uppercase tracking-wider">Total</p>
              <p className="text-[#F5F4F1] font-display font-semibold tabular-nums text-lg">{data.summary?.total || 0}</p>
            </div>
            <div className="bg-[#39FF14]/10 border border-[#39FF14]/25 rounded-lg p-2.5 text-center">
              <p className="text-[#39FF14]/80 text-[10px] uppercase tracking-wider">OK</p>
              <p className="text-[#39FF14] font-display font-semibold tabular-nums text-lg">{data.summary?.ok || 0}</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 text-center">
              <p className="text-red-400/80 text-[10px] uppercase tracking-wider">KO</p>
              <p className="text-red-400 font-display font-semibold tabular-nums text-lg">{data.summary?.ko || 0}</p>
            </div>
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-2.5 text-center">
              <p className="text-[#F5F4F1]/55 text-[10px] uppercase tracking-wider">Skipped</p>
              <p className="text-[#F5F4F1]/65 font-display font-semibold tabular-nums text-lg">{data.summary?.skipped || 0}</p>
            </div>
          </div>

          {/* Détails par source */}
          <div className="space-y-1.5 mt-3">
            {Object.entries(data.sources || {}).map(([name, src]) => {
              const statusColors = {
                ok: { dot: "bg-[#39FF14]", text: "text-[#39FF14]", border: "border-[#39FF14]/20" },
                ko: { dot: "bg-red-400", text: "text-red-400", border: "border-red-500/25" },
                skipped: { dot: "bg-[#F5F4F1]/30", text: "text-[#F5F4F1]/45", border: "border-warm" },
              };
              const c = statusColors[src.status] || statusColors.skipped;
              return (
                <div key={name} className={`flex items-center gap-3 py-2 px-3 bg-[#0E0D0B] border ${c.border} rounded-lg`}>
                  <div className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F4F1] text-xs font-medium truncate">{name}</p>
                    <p className={`text-[11px] truncate ${c.text}`}>
                      {src.message || src.reason || (src.status === "ok" ? "OK" : src.status === "ko" ? "Erreur" : "Non testé")}
                    </p>
                  </div>
                  {typeof src.duration_ms === "number" && (
                    <span className="text-[#F5F4F1]/35 text-[10px] font-mono flex-shrink-0 tabular-nums">{src.duration_ms}ms</span>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-[10px] text-[#F5F4F1]/30 italic mt-2 tabular-nums">
            Testé le {new Date(data.checked_at).toLocaleString("fr-FR")}
          </p>
        </>
      )}

      {!data && !loading && !error && (
        <div className="text-center py-6 text-[#F5F4F1]/40 text-sm">
          Lance le test pour vérifier l'état de toutes les sources de scraping.
        </div>
      )}
    </div>
  );
}

function SiteHealthSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/site-health")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 5 * 60 * 1000);  // refresh auto toutes les 5min
    return () => clearInterval(i);
  }, [refresh]);

  if (loading && !data) {
    return <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 flex items-center justify-center"><div className="w-5 h-5 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" /></div>;
  }
  if (!data || data.error) {
    return (
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
        <p className="text-[#F5F4F1]/65 text-sm">Surveillance indisponible : {data?.error || "erreur"}</p>
        <button onClick={refresh} className="mt-2 text-[#00E5FF] text-xs hover:underline">Réessayer</button>
      </div>
    );
  }

  const PLATFORM_META = {
    tiktok: { label: "TikTok", icon: "🎵" },
    instagram: { label: "Instagram", icon: "📸" },
    youtube: { label: "YouTube", icon: "▶️" },
  };

  const { summary, scrapes_by_platform, watchdog, scheduling } = data;

  return (
    <div className="space-y-4">
      {/* Alarme Apify si utilise */}
      <ApifyAlarmBanner />

      {/* Header simple : compteurs reels */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">État du site (données réelles)</p>
            <p className="text-xs text-[#F5F4F1]/55 mt-1 tabular-nums">
              {summary.total_clippers?.toLocaleString("fr-FR")} clippeurs <span className="text-[#F5F4F1]/25">·</span> {summary.active_campaigns} campagnes actives <span className="text-[#F5F4F1]/25">·</span> {summary.total_tracked_accounts?.toLocaleString("fr-FR")} comptes trackés
            </p>
          </div>
          <button onClick={refresh} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0E0D0B] hover:bg-[#262320] border border-warm text-[#F5F4F1] text-sm transition-all duration-150 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "…" : "Actualiser"}
          </button>
        </div>
      </div>

      {/* Compteurs scrapes par plateforme (REEL, sans % vs capacite estimee) */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
        <p className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">Scrapes réels par plateforme</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(scrapes_by_platform || {}).map(([plat, p]) => {
            const meta = PLATFORM_META[plat] || { label: plat, icon: "" };
            return (
              <div key={plat} className="rounded-lg border border-warm bg-[#0E0D0B] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{meta.icon}</span>
                  <span className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">{meta.label}</span>
                </div>
                <div className="space-y-1.5 text-xs text-[#F5F4F1]/60">
                  <div className="flex justify-between">
                    <span>Scrapes ce mois</span>
                    <span className="font-mono text-[#F5F4F1] tabular-nums">{(p.month_calls || 0).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Aujourd'hui</span>
                    <span className="font-mono text-[#F5F4F1] tabular-nums">{(p.today_calls || 0).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Comptes trackés</span>
                    <span className="font-mono text-[#F5F4F1] tabular-nums">{(p.accounts_tracked || 0).toLocaleString("fr-FR")}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Watchdog : derniers tests reels (pas de verdict couleur arbitraire) */}
      <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">Derniers tests Watchdog</p>
          <button
            onClick={() => adminFetch("/admin/run-watchdog-now", { method: "POST" }).then(refresh).catch(() => {})}
            className="text-xs px-2.5 py-1.5 rounded-md bg-[#0E0D0B] hover:bg-[#262320] border border-warm text-[#F5F4F1]/70 hover:text-[#F5F4F1] transition-all duration-150">
            Tester maintenant
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(watchdog || {}).map(([plat, w]) => {
            const meta = PLATFORM_META[plat] || { label: plat, icon: "" };
            const isOk = w.success === true;
            const hasResult = w.success !== undefined;
            return (
              <div key={plat} className={`rounded-lg border p-3 ${
                hasResult && isOk ? "bg-[#39FF14]/8 border-[#39FF14]/25" :
                hasResult && !isOk ? "bg-red-500/8 border-red-500/25" :
                "bg-[#0E0D0B] border-warm"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#F5F4F1] font-medium text-sm">{meta.icon} {meta.label}</span>
                  {hasResult && (
                    <span className={`text-[11px] font-medium ${isOk ? "text-[#39FF14]" : "text-red-400"}`}>
                      {isOk ? "Test OK" : "Test échec"}
                    </span>
                  )}
                </div>
                {w.timestamp && (
                  <p className="text-[10px] text-[#F5F4F1]/45 tabular-nums">
                    Dernier test : {new Date(w.timestamp).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </p>
                )}
                {w.video_count !== undefined && (
                  <p className="text-[10px] text-[#F5F4F1]/45 tabular-nums">{w.video_count} vidéos · {w.duration_ms || 0}ms</p>
                )}
                {w.error && <p className="text-[10px] text-red-400 truncate" title={w.error}>{w.error}</p>}
                {!hasResult && <p className="text-[10px] text-[#F5F4F1]/35">{w.message || "pas encore testé"}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scheduling : juste les compteurs, pas de qualité spread arbitraire */}
      {scheduling && (
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-3">
          <p className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">Planning scraping</p>
          <div className="flex justify-between text-xs">
            <span className="text-[#F5F4F1]/60">Campagnes en retard</span>
            <span className={`font-mono font-semibold tabular-nums ${(scheduling.overdue_campaigns || 0) > 5 ? "text-red-400" : "text-[#F5F4F1]"}`}>
              {scheduling.overdue_campaigns || 0}
            </span>
          </div>
        </div>
      )}

      {/* 🚨 MEGA DIAGNOSTIC : EN HAUT DE PAGE car le user dit "rien ne marche" */}
      <MegaDiagnosticSection />

      {/* 🌟 TEST META BUSINESS DISCOVERY (Insta 99%) */}
      <MetaBusinessTestSection />

      {/* 🔥 STRESS TEST 3 PLATEFORMES 40x chacune */}
      <MultiPlatformStressTestSection />

      {/* 🔬 TEST APPROFONDI proxy + VPS — pour identifier la cause exacte du 'rien ne marche' */}
      <ProxyVpsDeepTestSection />

      {/* Health check global de toutes les sources de scraping */}
      <ScrapingHealthCheckSection />

      {/* Test scraping autonome */}
      <InstaHealthSection />

      {/* Compteurs reels (Apify, alertes, circuit breakers) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`bg-[#1C1A17]/85 border rounded-xl p-4 ${summary.apify_used_today > 0 ? "border-[#FFB300]/30" : "border-warm"}`}>
          <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-wider">Apify utilisé ajd</p>
          <p className={`text-2xl font-display font-semibold tabular-nums tracking-tight mt-1 ${summary.apify_used_today > 0 ? "text-[#FFB300]" : "text-[#F5F4F1]"}`}>
            {summary.apify_used_today || 0}
          </p>
        </div>
        <div className={`bg-[#1C1A17]/85 border rounded-xl p-4 ${summary.open_alerts_critical > 0 ? "border-red-500/30" : "border-warm"}`}>
          <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-wider">Alertes ouvertes</p>
          <p className={`text-2xl font-display font-semibold tabular-nums tracking-tight mt-1 ${summary.open_alerts_critical > 0 ? "text-red-400" : "text-[#F5F4F1]"}`}>
            {summary.open_alerts_critical || 0}
          </p>
        </div>
        <div className={`bg-[#1C1A17]/85 border rounded-xl p-4 ${summary.circuit_breakers_active > 0 ? "border-[#FFB300]/30" : "border-warm"}`}>
          <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-wider">Circuit breakers</p>
          <p className={`text-2xl font-display font-semibold tabular-nums tracking-tight mt-1 ${summary.circuit_breakers_active > 0 ? "text-[#FFB300]" : "text-[#F5F4F1]"}`}>
            {summary.circuit_breakers_active || 0}
          </p>
        </div>
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4">
          <p className="text-[11px] text-[#F5F4F1]/55 uppercase tracking-wider">Clippeurs actifs 30 j</p>
          <p className="text-2xl font-display font-semibold tabular-nums tracking-tight mt-1 text-[#F5F4F1]">{summary.active_clippers_30d?.toLocaleString("fr-FR") || 0}</p>
          <p className="text-[10px] text-[#F5F4F1]/45 tabular-nums">/ {summary.total_clippers?.toLocaleString("fr-FR") || 0} total</p>
        </div>
      </div>

      <p className="text-[10px] text-[#F5F4F1]/30 italic text-center pt-2">
        Toutes les données ci-dessus sont des compteurs réels en base, sans estimation ni seuil arbitraire.
      </p>
    </div>
  );
}

function InstaHealthSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);

  const runTest = useCallback(() => {
    setLoading(true);
    setData(null);
    adminFetch("/admin/insta-health-full")
      .then(setData)
      .catch((e) => toast.error("Test failed : " + (e.message || "erreur")))
      .finally(() => setLoading(false));
  }, []);

  const runGlobalTest = useCallback(() => {
    setGlobalLoading(true);
    setGlobalData(null);
    adminFetch("/admin/scraping-health-all")
      .then(setGlobalData)
      .catch((e) => toast.error("Test global failed : " + (e.message || "erreur")))
      .finally(() => setGlobalLoading(false));
  }, []);

  const [vpsData, setVpsData] = useState(null);
  const [vpsLoading, setVpsLoading] = useState(false);
  const runVpsTest = useCallback(() => {
    setVpsLoading(true);
    setVpsData(null);
    adminFetch("/admin/vps-health")
      .then(setVpsData)
      .catch((e) => toast.error("Test VPS failed : " + (e.message || "erreur")))
      .finally(() => setVpsLoading(false));
  }, []);

  const [resetAllLoading, setResetAllLoading] = useState(false);
  const handleResetAllCampaigns = useCallback(() => {
    if (!window.confirm(
      "🚨 PURGE TOTALE — DERNIER AVERTISSEMENT 🚨\n\n" +
      "Cette action va effacer TOUTES les stats de TOUTES les campagnes :\n" +
      "- Toutes les vidéos trackées\n" +
      "- Tous les snapshots quotidiens\n" +
      "- Tous les budget_used remis à 0\n\n" +
      "Un re-scrape complet sera lancé en background (2-10 min).\n\n" +
      "Confirmer la purge globale ?"
    )) return;
    setResetAllLoading(true);
    adminFetch("/admin/reset-all-tracking-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirm: true }),
    })
      .then((d) => {
        const del = d?.deleted || {};
        toast.success(
          `✓ Purge effectuée : ${del.tracked_videos || 0} vidéos + ${(del.video_snapshots || 0) + (del.campaign_snapshots || 0) + (del.user_snapshots || 0)} snapshots supprimés. Re-scrape lancé.`,
          { duration: 10000 }
        );
      })
      .catch((e) => toast.error("Reset all failed : " + (e.message || "erreur")))
      .finally(() => setResetAllLoading(false));
  }, []);

  const retryFailed = useCallback(() => {
    setRetrying(true);
    setRetryResult(null);
    adminFetch("/admin/retry-failed-insta", { method: "POST" })
      .then((r) => {
        setRetryResult(r);
        toast.success(`Retry : ${r.ok || 0} OK / ${r.failed || 0} echec`);
        runTest();  // refresh apres retry
      })
      .catch((e) => toast.error("Retry failed : " + (e.message || "erreur")))
      .finally(() => setRetrying(false));
  }, [runTest]);

  return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[#F5F4F1] font-display font-semibold text-base tracking-tight">Diagnostic scraping (Insta + TikTok + YouTube)</p>
          <p className="text-xs text-[#F5F4F1]/55 mt-1 leading-relaxed">
            Teste chaque source en live. Identifie ce qui casse et propose le fix.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runGlobalTest}
            disabled={globalLoading}
            title="Tester les 3 plateformes (Insta + TikTok + YouTube) en parallèle"
            className="px-3 py-2 rounded-lg bg-[#00E5FF]/15 hover:bg-[#00E5FF]/25 text-[#00E5FF] border border-[#00E5FF]/30 text-sm font-medium transition-all duration-150 disabled:opacity-50"
          >
            {globalLoading ? "Test 3 plateformes…" : "Test 3 plateformes"}
          </button>
          <button
            onClick={runVpsTest}
            disabled={vpsLoading}
            title="Tester la santé du VPS"
            className="px-3 py-2 rounded-lg bg-[#FF007F]/15 hover:bg-[#FF007F]/25 text-[#FF007F] border border-[#FF007F]/30 text-sm font-medium transition-all duration-150 disabled:opacity-50"
          >
            {vpsLoading ? "Test VPS…" : "Tester VPS"}
          </button>
          <button
            onClick={handleResetAllCampaigns}
            disabled={resetAllLoading}
            title="Purge totale : efface toutes les stats de toutes les campagnes et relance un scrape complet"
            className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50 border border-red-400/40 transition-all duration-150"
          >
            {resetAllLoading ? "Purge…" : "Reset toutes campagnes"}
          </button>
          <button
            onClick={runTest}
            disabled={loading}
            title="Lancer un diagnostic Insta détaillé"
            className="px-3 py-2 rounded-lg bg-[#FF007F] hover:bg-[#E50073] text-white text-sm font-medium disabled:opacity-50 transition-all duration-150"
          >
            {loading ? "Test Insta…" : "Test Insta détaillé"}
          </button>
          {data?.failed_accounts_count > 0 && (
            <button
              onClick={retryFailed}
              disabled={retrying}
              title="Re-tenter le scraping des comptes en erreur"
              className="px-3 py-2 rounded-lg bg-[#FFB300]/15 hover:bg-[#FFB300]/25 text-[#FFB300] text-sm border border-[#FFB300]/25 disabled:opacity-50 transition-all duration-150"
            >
              {retrying ? "Retry…" : `Retry ${data.failed_accounts_count} comptes Insta`}
            </button>
          )}
        </div>
      </div>

      {/* Resultat test VPS */}
      {vpsData && (
        <div className={`rounded-lg border p-4 ${
          vpsData.verdict?.includes("✅") ? "bg-[#39FF14]/10 border-[#39FF14]/30" :
          vpsData.verdict?.includes("🚨") ? "bg-red-500/10 border-red-500/30" :
          "bg-[#FFB300]/10 border-[#FFB300]/30"
        }`}>
          <p className={`font-display font-semibold text-sm tracking-tight mb-2 ${
            vpsData.verdict?.includes("✅") ? "text-[#39FF14]" :
            vpsData.verdict?.includes("🚨") ? "text-red-400" : "text-[#FFB300]"
          }`}>{vpsData.verdict}</p>
          {vpsData.fix && (
            <p className="text-[#F5F4F1]/70 text-xs mb-2">Fix : {vpsData.fix}</p>
          )}
          {vpsData.vps_url && (
            <p className="text-[10px] text-[#F5F4F1]/45 mb-2 font-mono">URL : {vpsData.vps_url}</p>
          )}
          <div className="space-y-1">
            {(vpsData.tests || []).map((t, i) => (
              <div key={i} className={`text-xs px-2 py-1.5 rounded-md border ${
                t.ok ? "bg-[#39FF14]/8 text-[#39FF14] border-[#39FF14]/20" : "bg-red-500/8 text-red-400 border-red-500/25"
              }`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{t.ok ? "OK" : "KO"} <span className="ml-1">{t.name}</span></span>
                  {t.duration_ms !== undefined && <span className="text-[10px] opacity-60 tabular-nums">{t.duration_ms}ms</span>}
                </div>
                {t.result && <p className="text-[10px] opacity-80 mt-0.5">{t.result}</p>}
                {t.error && <p className="text-[10px] opacity-80 mt-0.5">err: {t.error.substring(0, 200)}</p>}
                {!t.ok && t.fix_if_failed && (
                  <p className="text-[10px] text-[#FFB300] mt-1">Fix : {t.fix_if_failed}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resultat test global 3 plateformes */}
      {globalData && (
        <div className="space-y-3">
          {/* Verdict */}
          <div className={`p-3 rounded-lg border ${
            globalData.verdict?.includes("🚨") ? "bg-red-500/10 border-red-500/30" :
            globalData.verdict?.includes("⚠️") ? "bg-[#FFB300]/10 border-[#FFB300]/30" :
            "bg-[#39FF14]/10 border-[#39FF14]/30"
          }`}>
            <p className={`font-display font-semibold tracking-tight ${
              globalData.verdict?.includes("🚨") ? "text-red-400" :
              globalData.verdict?.includes("⚠️") ? "text-[#FFB300]" : "text-[#39FF14]"
            }`}>{globalData.verdict}</p>
            {globalData.recommendation && (
              <p className="text-[#F5F4F1]/70 text-xs mt-2">{globalData.recommendation}</p>
            )}
          </div>

          {/* Config globale */}
          {globalData.config_global && (
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
              <p className="text-[11px] text-[#F5F4F1]/55 font-display font-semibold tracking-tight uppercase tracking-wider mb-2">Config env Railway</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                {Object.entries(globalData.config_global).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="text-[#F5F4F1]/55 truncate">{k}</span>
                    <span className={`font-mono tabular-nums ${typeof v === "string" && v.includes("❌") ? "text-red-400" : typeof v === "string" && v.includes("✅") ? "text-[#39FF14]" : "text-[#F5F4F1]"}`}>
                      {typeof v === "boolean" ? (v ? "true" : "false") : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3 plateformes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {Object.entries(globalData.platforms || {}).map(([plat, info]) => {
              const platMeta = { instagram: { icon: "📸", label: "Instagram" }, tiktok: { icon: "🎵", label: "TikTok" }, youtube: { icon: "▶️", label: "YouTube" } }[plat] || { icon: "?", label: plat };
              return (
                <div key={plat} className={`rounded-lg border p-3 ${info.any_working ? "bg-[#39FF14]/8 border-[#39FF14]/25" : "bg-red-500/8 border-red-500/25"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">{platMeta.icon} {platMeta.label}</p>
                    <span className={`text-[11px] font-semibold ${info.any_working ? "text-[#39FF14]" : "text-red-400"}`}>
                      {info.any_working ? "OK" : "HS"}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#F5F4F1]/45 mb-2">Test : @{info.test_account}</p>
                  <div className="space-y-1">
                    {(info.sources || []).map((s, i) => (
                      <div key={i} className={`text-xs px-2 py-1 rounded-md border ${
                        s.skipped ? "bg-[#0E0D0B] text-[#F5F4F1]/45 border-warm" :
                        s.ok ? "bg-[#39FF14]/8 text-[#39FF14] border-[#39FF14]/20" : "bg-red-500/8 text-red-400 border-red-500/25"
                      }`}>
                        <div className="flex items-center justify-between gap-1">
                          <span className="truncate">{s.skipped ? "—" : s.ok ? "OK" : "KO"} <span className="ml-1">{s.name}</span></span>
                          {s.duration_ms !== undefined && <span className="text-[9px] opacity-60 tabular-nums">{s.duration_ms}ms</span>}
                        </div>
                        {s.result && <p className="text-[10px] opacity-80 mt-0.5">{s.result}</p>}
                        {s.error && <p className="text-[10px] opacity-80 mt-0.5">err: {s.error.substring(0, 80)}</p>}
                        {!s.ok && s.fix_if_failed && (
                          <p className="text-[10px] text-[#FFB300] mt-0.5">Fix : {s.fix_if_failed}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {(globalData.failed_accounts_by_platform?.[plat] || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-warm">
                      <p className="text-[10px] text-red-400 font-semibold mb-1">
                        {globalData.failed_accounts_by_platform[plat].length} compte{globalData.failed_accounts_by_platform[plat].length > 1 ? "s" : ""} en erreur
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-0.5">
                        {globalData.failed_accounts_by_platform[plat].slice(0, 5).map((a, i) => (
                          <div key={i} className="text-[10px] bg-[#0E0D0B] border border-warm rounded p-1.5">
                            <p className="text-[#F5F4F1] truncate">@{a.username}</p>
                            <p className="text-red-400/70 truncate">{a.error}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!data && !loading && (
        <p className="text-xs text-[#F5F4F1]/45 text-center py-3">Lance un test complet pour voir l'état des sources.</p>
      )}

      {data && (
        <>
          {/* Verdict global */}
          <div className={`p-3 rounded-lg border ${
            data.verdict?.includes("🚨") ? "bg-red-500/10 border-red-500/30" :
            data.verdict?.includes("⚠️") ? "bg-[#FFB300]/10 border-[#FFB300]/30" :
            "bg-[#39FF14]/10 border-[#39FF14]/30"
          }`}>
            <p className={`font-display font-semibold tracking-tight ${
              data.verdict?.includes("🚨") ? "text-red-400" :
              data.verdict?.includes("⚠️") ? "text-[#FFB300]" : "text-[#39FF14]"
            }`}>{data.verdict}</p>
            {data.recommendation && (
              <p className="text-[#F5F4F1]/70 text-xs mt-2">{data.recommendation}</p>
            )}
          </div>

          {/* Config */}
          {data.config && (
            <div className="bg-[#0E0D0B] border border-warm rounded-lg p-3">
              <p className="text-[11px] text-[#F5F4F1]/55 font-display font-semibold uppercase tracking-wider mb-2">Configuration env</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                {Object.entries(data.config).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="text-[#F5F4F1]/55 truncate">{k}</span>
                    <span className={`font-mono tabular-nums ${typeof v === "string" && v.includes("❌") ? "text-red-400" : typeof v === "string" && v.includes("✅") ? "text-[#39FF14]" : "text-[#F5F4F1]"}`}>
                      {typeof v === "boolean" ? (v ? "true" : "false") : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources testees */}
          <div className="space-y-2">
            <p className="text-[11px] text-[#F5F4F1]/55 font-display font-semibold uppercase tracking-wider">Sources testées sur @{data.test_username}</p>
            {(data.sources || []).map((s, i) => (
              <div key={i} className={`p-3 rounded-lg border text-sm ${
                s.skipped ? "bg-[#0E0D0B] border-warm" :
                s.ok ? "bg-[#39FF14]/8 border-[#39FF14]/25" : "bg-red-500/8 border-red-500/25"
              }`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`font-display font-semibold tracking-tight ${s.skipped ? "text-[#F5F4F1]/45" : s.ok ? "text-[#39FF14]" : "text-red-400"}`}>
                    {s.skipped ? "—" : s.ok ? "OK" : "KO"} <span className="ml-1">{s.name}</span>
                  </span>
                  {s.duration_ms !== undefined && (
                    <span className="text-[10px] text-[#F5F4F1]/45 font-mono tabular-nums">{s.duration_ms}ms</span>
                  )}
                </div>
                {s.result && <p className="text-xs text-[#F5F4F1]/70 mt-1">{s.result}</p>}
                {s.error && <p className="text-xs text-red-400 mt-1">Erreur : {s.error}</p>}
                {s.reason && <p className="text-xs text-[#F5F4F1]/55 mt-1 italic">{s.reason}</p>}
                {!s.ok && s.fix_if_failed && (
                  <p className="text-[11px] text-[#FFB300] mt-1.5">Fix : {s.fix_if_failed}</p>
                )}
              </div>
            ))}
          </div>

          {/* Comptes en erreur dans la DB */}
          {data.failed_accounts_count > 0 && (
            <div className="bg-red-500/8 border border-red-500/25 rounded-lg p-3">
              <p className="text-red-400 font-display font-semibold text-sm tracking-tight mb-2">
                {data.failed_accounts_count} compte{data.failed_accounts_count > 1 ? "s" : ""} Insta en erreur dans la DB
              </p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {(data.failed_accounts_in_db || []).map((acc, i) => (
                  <div key={i} className="text-xs bg-[#0E0D0B] border border-warm rounded p-2">
                    <p className="text-[#F5F4F1]">@{acc.username} <span className="text-[#F5F4F1]/45 ml-2">({acc.status})</span></p>
                    <p className="text-red-400/80 text-[10px] mt-0.5 truncate" title={acc.last_scrape_error}>
                      {acc.last_scrape_error || "?"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {retryResult && (
            <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/25 rounded-lg p-3 text-xs">
              <p className="text-[#00E5FF] font-display font-semibold tracking-tight">Résultat retry</p>
              <p className="text-[#F5F4F1]/70 tabular-nums">
                Re-scrapé : {retryResult.retried || 0} <span className="text-[#F5F4F1]/25 mx-1">·</span> OK : {retryResult.ok || 0} <span className="text-[#F5F4F1]/25 mx-1">·</span> Échec : {retryResult.failed || 0}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ApiCapacitySection() {
  // Conserve pour rétro-compat — redirige vers SiteHealthSection
  return <SiteHealthSection />;
}

function UsageMonitorTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/usage-monitor")
      .then(setData)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const barColor = (pct) => pct < 60 ? "#39FF14" : pct < 80 ? "#FFB300" : "#FF4757";
  const Bar = ({ pct, color }) => (
    <div className="w-full h-2 bg-[#0E0D0B] border border-warm rounded-full overflow-hidden">
      <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, pct || 0)}%`, background: color || barColor(pct) }} />
    </div>
  );

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" /></div>;
  }
  if (!data) return (
    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-8 text-center">
      <p className="text-[#F5F4F1]/55 text-sm">Aucune donnée disponible.</p>
      <button onClick={refresh} className="mt-3 px-4 py-2 rounded-lg bg-[#FF007F] hover:bg-[#E50073] text-white text-sm font-medium transition-all duration-150">Réessayer</button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
              <TrendingUp className="w-4.5 h-4.5 text-[#FF007F]" />
            </div>
            <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Capacité & Coûts</h2>
          </div>
          <p className="text-[#F5F4F1]/55 text-sm">Utilisation actuelle des APIs et recommandations d'upgrade.</p>
        </div>
        <button onClick={refresh} disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1] text-sm transition-all duration-150 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Chargement…" : "Actualiser"}
        </button>
      </div>

      {/* NEW : Capacite mensuelle scraping (TT/IG/YT en %) */}
      <ApiCapacitySection />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 hover:border-[#F5F4F1]/14 hover:-translate-y-0.5 transition-all duration-200">
          <p className="text-[11px] text-[#F5F4F1]/55 mb-1.5 uppercase tracking-wider">Clippeurs actifs</p>
          <p className="text-2xl font-display font-semibold text-[#F5F4F1] tabular-nums tracking-tight">{data.clippers_active}</p>
        </div>
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 hover:border-[#F5F4F1]/14 hover:-translate-y-0.5 transition-all duration-200">
          <p className="text-[11px] text-[#F5F4F1]/55 mb-1.5 uppercase tracking-wider">Campagnes actives</p>
          <p className="text-2xl font-display font-semibold text-[#FF007F] tabular-nums tracking-tight">{data.campaigns_active}</p>
        </div>
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 hover:border-[#F5F4F1]/14 hover:-translate-y-0.5 transition-all duration-200">
          <p className="text-[11px] text-[#F5F4F1]/55 mb-1.5 uppercase tracking-wider">Vidéos trackées</p>
          <p className="text-2xl font-display font-semibold text-[#00E5FF] tabular-nums tracking-tight">{(data.total_videos_tracked || 0).toLocaleString("fr-FR")}</p>
        </div>
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 hover:border-[#F5F4F1]/14 hover:-translate-y-0.5 transition-all duration-200">
          <p className="text-[11px] text-[#F5F4F1]/55 mb-1.5 uppercase tracking-wider">Coût mensuel (estim.)</p>
          <p className="text-2xl font-display font-semibold text-[#39FF14] tabular-nums tracking-tight">{data.total_monthly_cost_eur || 0} €</p>
        </div>
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
          <p className="text-sm font-display font-semibold tracking-tight text-[#F5F4F1] mb-3">Recommandations</p>
          <div className="space-y-2">
            {data.recommendations.map((r, i) => (
              <div key={i} className={`px-3 py-2.5 rounded-lg text-sm leading-relaxed ${r.startsWith("✅") ? "bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/25" : "bg-[#FFB300]/10 text-[#FFB300] border border-[#FFB300]/25"}`}>
                {r}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service cards */}
      <div className="space-y-3">
        {Object.entries(data.services || {}).map(([key, svc]) => {
          const pct = typeof svc.percent_used === "number" ? svc.percent_used : 0;
          const isError = svc.status === "error";
          const isNotConfig = svc.status === "not_configured";
          return (
            <div key={key} className="bg-[#1C1A17]/85 border border-warm rounded-xl p-5">
              <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">{svc.name}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[11px] text-[#F5F4F1]/55 tabular-nums">{svc.cost_per_month_eur || 0} €/mois</span>
                    {isError && <span className="text-[10px] px-2 py-0.5 rounded-md bg-red-500/10 text-red-400 border border-red-500/25 font-medium">Erreur</span>}
                    {isNotConfig && <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#FFB300]/10 text-[#FFB300] border border-[#FFB300]/25 font-medium">Non configuré</span>}
                    {!isError && !isNotConfig && <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/25 font-medium">OK</span>}
                  </div>
                </div>
                {typeof svc.percent_used === "number" && (
                  <span className="text-xl font-display font-semibold tabular-nums tracking-tight" style={{ color: barColor(pct) }}>{pct}%</span>
                )}
              </div>
              {typeof svc.percent_used === "number" && <Bar pct={pct} />}

              {/* Détails par service */}
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                {svc.capacity_clippers && (
                  <div><span className="text-[#F5F4F1]/45">Capacité max </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.capacity_clippers} clippeurs</span></div>
                )}
                {svc.bandwidth_estimated_gb !== undefined && (
                  <div><span className="text-[#F5F4F1]/45">Bandwidth </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.bandwidth_estimated_gb} / {svc.bandwidth_total_gb} GB</span></div>
                )}
                {svc.monthly_usage_usd !== undefined && (
                  <div><span className="text-[#F5F4F1]/45">Apify </span><span className="text-[#F5F4F1] font-mono tabular-nums">${svc.monthly_usage_usd} / ${svc.monthly_credit_usd}</span></div>
                )}
                {svc.quota_used_estimated !== undefined && (
                  <div><span className="text-[#F5F4F1]/45">Quota/jour </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.quota_used_estimated} / {svc.quota_per_day}</span></div>
                )}
                {svc.uptime_hours !== undefined && (
                  <div><span className="text-[#F5F4F1]/45">Uptime </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.uptime_hours}h</span></div>
                )}
                {svc.concurrent_now !== undefined && (
                  <div><span className="text-[#F5F4F1]/45">Concurrent </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.concurrent_now} / {svc.concurrent_max}</span></div>
                )}
                {svc.proxy && (
                  <div><span className="text-[#F5F4F1]/45">Proxy </span><span className="text-[#F5F4F1] font-mono">{svc.proxy}</span></div>
                )}
                {svc.ip_count && (
                  <div><span className="text-[#F5F4F1]/45">IPs </span><span className="text-[#F5F4F1] font-mono tabular-nums">{svc.ip_count}</span></div>
                )}
                {svc.error && (
                  <div className="col-span-full"><span className="text-red-400 text-xs">{svc.error}</span></div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-[#F5F4F1]/35 text-center tabular-nums pt-2">Mise à jour : {new Date(data.timestamp).toLocaleString("fr-FR")}</p>
    </div>
  );
}


function ApiStatusTab() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage]     = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const testAll = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/api-status")
      .then(setStatus)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const fetchUsage = useCallback(() => {
    setUsageLoading(true);
    adminFetch("/admin/api-usage")
      .then(setUsage)
      .catch(() => {})
      .finally(() => setUsageLoading(false));
  }, []);

  useEffect(() => { testAll(); fetchUsage(); }, [testAll, fetchUsage]);

  const apis = [
    { key: "mongodb",      label: "MongoDB",                          icon: Database,   desc: "Base de données principale (Railway)" },
    { key: "clipscraper",  label: "ClipScraper VPS Hostinger",        icon: Globe,      desc: "Scraping principal TikTok+Insta+YouTube (proxy Webshare)" },
    { key: "youtube_api",  label: "YouTube Data API v3",              icon: Youtube,    desc: "Stats YouTube (gratuit, 10 000 req/jour)" },
    { key: "apify",        label: "Apify (BACKUP uniquement)",        icon: Zap,        desc: "Dernier recours - $5/mois gratuit, devrait quasi jamais être appelé" },
    { key: "google_oauth", label: "Google OAuth",                     icon: Shield,     desc: "Connexion Google (login)" },
    { key: "gocardless",   label: "GoCardless (SEPA)",                icon: CreditCard, desc: "Paiements SEPA + abonnements récurrents (sandbox/live)" },
  ];

  const usageServices = [
    { key: "youtube",   label: "YouTube API",             color: "#FF0000", icon: "▶" },
    { key: "apify",     label: "Apify (TikTok + Insta)",  color: "#00E5FF", icon: "⚡" },
    { key: "resend",    label: "Resend (Emails)",          color: "#9B59B6", icon: "✉" },
  ];

  function StatusBadge({ s }) {
    if (!s) return <span className="text-[#F5F4F1]/35 text-xs">Non testé</span>;
    if (s.status === "ok") return (
      <span className="inline-flex items-center gap-1.5 text-[#39FF14] text-xs font-medium bg-[#39FF14]/10 border border-[#39FF14]/25 px-2 py-0.5 rounded-md">
        <CheckCircle2 className="w-3 h-3" /> OK {s.latency_ms && <span className="text-[#39FF14]/70 tabular-nums">· {s.latency_ms} ms</span>}
      </span>
    );
    if (s.status === "not_configured") return (
      <span className="inline-flex items-center gap-1.5 text-[#FFB300] text-xs font-medium bg-[#FFB300]/10 border border-[#FFB300]/25 px-2 py-0.5 rounded-md">
        <AlertCircle className="w-3 h-3" /> Non configuré
      </span>
    );
    if (s.status === "not_installed") return (
      <span className="inline-flex items-center gap-1.5 text-[#FFB300] text-xs font-medium bg-[#FFB300]/10 border border-[#FFB300]/25 px-2 py-0.5 rounded-md">
        <AlertCircle className="w-3 h-3" /> Non installé
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 text-red-400 text-xs font-medium bg-red-500/10 border border-red-500/25 px-2 py-0.5 rounded-md">
        <XCircle className="w-3 h-3" /> Erreur
      </span>
    );
  }

  function MiniBar({ value, max, color }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    const danger = pct > 80;
    return (
      <div className="w-full h-1.5 bg-[#0E0D0B] border border-warm rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: danger ? "#FF4757" : color }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div>
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
            <Plug className="w-4.5 h-4.5 text-[#00E5FF]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Connexions API</h2>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">État des services externes et utilisation par période.</p>
      </div>

      {/* ── Connexions ── */}
      <div>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="text-base font-display font-semibold tracking-tight text-[#F5F4F1]">Services connectés</h3>
          <button
            onClick={testAll}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-[#1C1A17] hover:bg-[#262320] border border-warm rounded-lg text-[#F5F4F1] text-sm transition-all duration-150 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Tout tester
          </button>
        </div>
        {status?.checked_at && (
          <p className="text-[#F5F4F1]/35 text-[11px] mb-4 flex items-center gap-1 tabular-nums">
            <Clock className="w-3 h-3" /> Dernier test : {formatDate(status.checked_at)}
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {apis.map(({ key, label, icon: Icon, desc }) => {
            const s = status?.[key];
            return (
              <div key={key} className="bg-[#1C1A17]/85 border border-warm hover:border-[#F5F4F1]/14 rounded-xl p-4 transition-all duration-150">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-[#0E0D0B] border border-warm rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#F5F4F1]/70" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#F5F4F1] font-medium text-sm truncate">{label}</p>
                      <p className="text-[#F5F4F1]/45 text-[11px] mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0"><StatusBadge s={s} /></div>
                </div>
                {s?.error && (
                  <p className="mt-3 text-[11px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-md px-3 py-2 leading-relaxed">
                    {s.error}
                  </p>
                )}
                {/* Apify: show plan + compute units */}
                {key === "apify" && s?.status === "ok" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.plan && (
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25 font-mono">
                        Plan {s.plan}
                      </span>
                    )}
                    {s.limit_usd > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#1C1A17] text-[#F5F4F1]/70 border border-warm font-mono tabular-nums">
                        CU ce mois {s.usage_usd} / {s.limit_usd}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Utilisation API ── */}
      <div>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-display font-semibold tracking-tight text-[#F5F4F1]">Utilisation des APIs</h3>
            <p className="text-[#F5F4F1]/45 text-xs mt-1">Nombre d'appels réels par période — temps réel.</p>
          </div>
          <button
            onClick={fetchUsage}
            disabled={usageLoading}
            className="flex items-center gap-2 px-3.5 py-2 bg-[#1C1A17] hover:bg-[#262320] border border-warm rounded-lg text-[#F5F4F1] text-sm transition-all duration-150 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${usageLoading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* Tableau principal */}
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm">
                  <th className="text-left px-5 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">Service</th>
                  <th className="text-center px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">Cette heure</th>
                  <th className="text-center px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">Aujourd'hui</th>
                  <th className="text-center px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">7 j</th>
                  <th className="text-center px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">30 j</th>
                  <th className="text-center px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">Succès</th>
                  <th className="text-left px-4 py-3 text-[#F5F4F1]/55 text-[11px] font-medium uppercase tracking-wider">Limite gratuite</th>
                </tr>
              </thead>
              <tbody>
                {usageLoading && !usage ? (
                  <tr><td colSpan={7} className="py-10 text-center text-[#F5F4F1]/45 text-xs">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-3 h-3 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
                      Chargement…
                    </div>
                  </td></tr>
                ) : usageServices.map(({ key, label, color, icon }) => {
                  const d = usage?.services?.[key];
                  const successRate = d?.success_rate ?? 100;
                  const errColor = successRate < 90 ? "text-red-400" : successRate < 99 ? "text-[#FFB300]" : "text-[#39FF14]";
                  return (
                    <tr key={key} className="border-b border-warm last:border-b-0 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span className="text-base" style={{ color }}>{icon}</span>
                          <div>
                            <p className="text-[#F5F4F1] text-xs font-medium">{label}</p>
                            <p className="text-[#F5F4F1]/45 text-[10px]">{d?.free_limit || "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[#F5F4F1] font-mono text-sm tabular-nums">{(d?.this_hour ?? 0).toLocaleString()}</span>
                        {d?.errors_hour > 0 && <span className="text-red-400 text-[10px] ml-1">({d.errors_hour} err)</span>}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[#F5F4F1] font-mono text-sm font-semibold tabular-nums">{(d?.today ?? 0).toLocaleString()}</span>
                        {d?.errors_today > 0 && <span className="text-red-400 text-[10px] ml-1">({d.errors_today} err)</span>}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[#F5F4F1]/70 font-mono text-sm tabular-nums">{(d?.week ?? 0).toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[#F5F4F1]/60 font-mono text-sm tabular-nums">{(d?.month ?? 0).toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`font-mono text-sm font-semibold tabular-nums ${errColor}`}>{successRate}%</span>
                      </td>
                      <td className="px-4 py-3.5">
                        {/* Mini progress bar — YouTube 10k/jour, Apify $5/mois, Resend 3000/mois */}
                        {key === "youtube" && <MiniBar value={d?.today ?? 0} max={10000} color={color} />}
                        {key === "apify"   && <MiniBar value={d?.today ?? 0} max={100}   color={color} />}
                        {key === "resend"  && <MiniBar value={d?.month ?? 0} max={3000}  color={color} />}
                        <p className="text-[#F5F4F1]/35 text-[10px] mt-1">{d?.free_limit}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mini-graphes 24h */}
        {usage && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {usageServices.map(({ key, label, color }) => {
              const hourly = usage?.services?.[key]?.hourly || [];
              const maxVal = Math.max(...hourly.map(h => h.calls), 1);
              return (
                <div key={key} className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4">
                  <p className="text-[#F5F4F1] text-xs font-medium mb-3">{label} <span className="text-[#F5F4F1]/40">· 24 h</span></p>
                  {hourly.length === 0 ? (
                    <p className="text-[#F5F4F1]/25 text-[10px] text-center py-4">Aucun appel</p>
                  ) : (
                    <div className="flex items-end gap-0.5 h-12">
                      {hourly.map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-sm min-w-[2px]"
                          style={{
                            height: `${Math.max((h.calls / maxVal) * 100, 4)}%`,
                            backgroundColor: h.errors > 0 ? "#FF4757" : color,
                            opacity: 0.7,
                          }}
                          title={`${h.date} ${h.hour}h : ${h.calls} appels${h.errors > 0 ? `, ${h.errors} erreurs` : ""}`}
                        />
                      ))}
                    </div>
                  )}
                  <p className="text-[#F5F4F1]/45 text-[10px] mt-2 text-right tabular-nums">
                    {(usage?.services?.[key]?.today ?? 0).toLocaleString()} aujourd'hui
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {usage?.fetched_at && (
          <p className="text-[#F5F4F1]/30 text-[10px] mt-3 flex items-center gap-1 tabular-nums">
            <Clock className="w-2.5 h-2.5" /> Données au {formatDate(usage.fetched_at)}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── PostsTab ────────────────────────────────────────────────────────────────

function PostsTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");

  const fetchPosts = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/posts")
      .then(setPosts)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleDelete = async (postId) => {
    try {
      await adminFetch(`/admin/posts/${postId}`, { method: "DELETE" });
      toast.success("Post supprimé");
      setConfirmDelete(null);
      fetchPosts();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = posts.filter(p =>
    !search ||
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.content?.toLowerCase().includes(search.toLowerCase()) ||
    p.agency_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
            <Eye className="w-4.5 h-4.5 text-[#FF007F]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Tous les posts</h2>
          <span className="text-[#F5F4F1]/40 text-sm tabular-nums ml-1">({filtered.length}/{posts.length})</span>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Posts publiés par les agences dans leurs campagnes — modération globale.</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher titre, contenu, agence…"
          className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22 flex-1 max-w-md"
        />
        <button onClick={fetchPosts} title="Recharger" className="p-2 bg-[#1C1A17] hover:bg-[#262320] border border-warm rounded-lg text-[#F5F4F1]/70 hover:text-[#F5F4F1] transition-all duration-150">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.length === 0 && (
            <div className="bg-[#1C1A17]/85 border border-warm rounded-xl py-16 text-center">
              <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mx-auto mb-3">
                <Eye className="w-4 h-4 text-[#F5F4F1]/30" />
              </div>
              <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun post</p>
              <p className="text-[#F5F4F1]/35 text-xs mt-1">Aucun post publié par les agences.</p>
            </div>
          )}
          {filtered.map((post) => (
            <div key={post.announcement_id} className="bg-[#1C1A17]/85 border border-warm hover:border-[#F5F4F1]/14 rounded-xl p-4 flex items-start gap-4 transition-all duration-150">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[11px] text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/20 px-2 py-0.5 rounded-md font-medium">{post.agency_name}</span>
                  <span className="text-[11px] text-[#F5F4F1]/45 tabular-nums">{formatDate(post.created_at)}</span>
                </div>
                <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight truncate">{post.title || "(sans titre)"}</p>
                <p className="text-[#F5F4F1]/60 text-xs mt-1 line-clamp-2 leading-relaxed">{post.content}</p>
              </div>
              <button
                onClick={() => setConfirmDelete(post)}
                title="Supprimer ce post"
                className="p-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all duration-150 flex-shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer ce post ?"
          message={`Supprimer le post "${confirmDelete.title || "(sans titre)"}" ?`}
          confirmLabel="Supprimer"
          danger
          onConfirm={() => handleDelete(confirmDelete.announcement_id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ─── AdminCampaignDetailPanel ─────────────────────────────────────────────────

function AdminCampaignDetailPanel({ campaignId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stats"); // stats | chat | membres | videos
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/admin/campaigns/${campaignId}/detail`)
      .then(setData)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => {
    if (tab === "chat" && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [tab, data?.messages]);

  const handleSendMsg = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const msg = await adminFetch(`/admin/campaigns/${campaignId}/send-message`, {
        method: "POST",
        body: JSON.stringify({ content: newMsg.trim() }),
      });
      setData(d => d ? { ...d, messages: [...(d.messages || []), msg] } : d);
      setNewMsg("");
      toast.success("Message envoyé");
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const isClick = data?.payment_model === "clicks";
  const cs = data?.click_stats || {};
  const vs = data?.view_stats || {};

  const statusColors = {
    active: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",
    paused: "bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/25",
    ended: "bg-red-500/10 text-red-400 border-red-500/25",
    draft: "bg-[#0E0D0B] text-[#F5F4F1]/55 border-warm",
  };
  const roleColors = {
    clipper: "text-[#00E5FF]",
    agency: "text-[#FF007F]",
    manager: "text-[#39FF14]",
    client: "text-[#FFB300]",
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="w-full max-w-2xl bg-[#0A0907] border-l border-warm flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-warm flex-shrink-0">
          {loading ? (
            <div className="flex items-center gap-2 text-[#F5F4F1]/55 text-sm">
              <div className="w-3 h-3 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
              Chargement…
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-[#F5F4F1] font-display font-semibold tracking-tight truncate">{data?.name || "—"}</h3>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-md border ${statusColors[data?.status] || statusColors.draft}`}>{data?.status}</span>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-md border ${isClick ? "bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/25" : "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/25"}`}>
                  {isClick ? "Au clic" : "Aux vues"}
                </span>
              </div>
              <p className="text-[#F5F4F1]/45 text-xs mt-1.5">Agence <span className="text-[#F5F4F1]/70 ml-1">{data?.agency_name}</span> <span className="mx-1.5 text-[#F5F4F1]/25">·</span> {data?.agency_email}</p>
            </div>
          )}
          <button onClick={onClose} title="Fermer" className="ml-4 p-2 rounded-lg bg-[#1C1A17] hover:bg-[#262320] border border-warm text-[#F5F4F1]/55 hover:text-[#F5F4F1] transition-all duration-150 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-warm flex-shrink-0 overflow-x-auto bg-[#0E0D0B]">
          {[
            { id: "stats", label: isClick ? "Clics & stats" : "Vues & stats" },
            { id: "videos", label: `Vidéos (${data?.tracked_videos?.length || 0})` },
            { id: "chat", label: `Chat (${data?.messages?.length || 0})` },
            { id: "membres", label: `Membres (${data?.members?.length || 0})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-[13px] font-medium transition-all duration-150 border-b-2 whitespace-nowrap ${tab === t.id ? "border-[#FF007F] text-[#F5F4F1]" : "border-transparent text-[#F5F4F1]/45 hover:text-[#F5F4F1]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-[#0E0D0B]">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
            </div>
          ) : !data ? null : (

            // ── STATS TAB ──
            tab === "stats" ? (
              <div className="p-5 space-y-4">
                {isClick ? (
                  <>
                    <div className="grid grid-cols-3 gap-2.5">
                      {[
                        { label: "Clics totaux", value: (cs.total_clicks || 0).toLocaleString("fr-FR"), color: "text-[#FFB300]", bg: "bg-[#FFB300]/8", border: "border-[#FFB300]/15" },
                        { label: "Clics uniques", value: (cs.unique_clicks || 0).toLocaleString("fr-FR"), color: "text-[#39FF14]", bg: "bg-[#39FF14]/8", border: "border-[#39FF14]/15" },
                        { label: "Gains générés", value: `€${(cs.earnings || 0).toFixed(2)}`, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/8", border: "border-[#00E5FF]/15" },
                      ].map(c => (
                        <div key={c.label} className={`${c.bg} border ${c.border} rounded-xl p-3.5`}>
                          <div className={`text-lg md:text-xl font-display font-semibold tabular-nums tracking-tight ${c.color}`}>{c.value}</div>
                          <div className="text-[#F5F4F1]/55 text-[11px] mt-1">{c.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 text-xs text-[#F5F4F1]/60">
                      <span className="text-[#F5F4F1] font-medium">Tarif </span>€{cs.rate_per_click || 0}/clic
                      <span className="mx-2 text-[#F5F4F1]/25">·</span>
                      <span className="text-[#F5F4F1] font-medium">Mode </span>
                      {cs.unique_clicks_only ? "Clics uniques uniquement (anti-spam)" : "Tous les clics"}
                    </div>
                    {/* Liens de tracking */}
                    {cs.links?.length > 0 && (
                      <div>
                        <p className="text-[#F5F4F1]/55 text-[11px] uppercase tracking-widest font-medium mb-2.5">Liens des clippeurs</p>
                        <div className="space-y-2">
                          {cs.links.map(lnk => (
                            <div key={lnk.link_id} className="bg-[#1C1A17]/85 border border-warm rounded-lg p-3 flex items-center gap-3">
                              <div className="w-7 h-7 rounded-full bg-[#FFB300]/15 border border-[#FFB300]/25 flex items-center justify-center text-xs font-display font-semibold text-[#FFB300] flex-shrink-0">
                                {(lnk.clipper_display_name || "?")[0]?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[#F5F4F1] text-xs font-medium truncate">{lnk.clipper_display_name}</p>
                                <p className="text-[#F5F4F1]/40 text-[10px] font-mono truncate">{lnk.tracking_url}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-[#FFB300] font-mono text-xs tabular-nums">{(lnk.click_count || 0).toLocaleString("fr-FR")} clics</p>
                                <p className="text-[#39FF14] font-mono text-[10px] tabular-nums">{(lnk.unique_click_count || 0).toLocaleString("fr-FR")} uniques</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2.5">
                      {[
                        { label: "Vues totales", value: fmtNum(vs.total_views || 0), color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/8", border: "border-[#00E5FF]/15" },
                        { label: "Vidéos", value: vs.video_count || 0, color: "text-[#F5F4F1]", bg: "bg-[#1C1A17]", border: "border-warm" },
                        { label: "Budget consommé", value: `€${(vs.budget_used || 0).toFixed(2)}`, color: "text-[#39FF14]", bg: "bg-[#39FF14]/8", border: "border-[#39FF14]/15" },
                      ].map(c => (
                        <div key={c.label} className={`${c.bg} border ${c.border} rounded-xl p-3.5`}>
                          <div className={`text-lg md:text-xl font-display font-semibold tabular-nums tracking-tight ${c.color}`}>{typeof c.value === "number" ? c.value.toLocaleString("fr-FR") : c.value}</div>
                          <div className="text-[#F5F4F1]/55 text-[11px] mt-1">{c.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4 text-xs text-[#F5F4F1]/60">
                      <span className="text-[#F5F4F1] font-medium">RPM </span>€{data.rpm || 0}/1K vues
                      <span className="mx-2 text-[#F5F4F1]/25">·</span>
                      <span className="text-[#F5F4F1] font-medium">Budget total </span>
                      {data.budget_unlimited ? "Illimité" : `€${data.budget_total || 0}`}
                    </div>
                  </>
                )}
                {/* Budget bar */}
                {!data.budget_unlimited && data.budget_total > 0 && (
                  <div className="bg-[#1C1A17]/85 border border-warm rounded-xl p-4">
                    <div className="flex justify-between text-xs text-[#F5F4F1]/55 mb-2">
                      <span>Budget consommé</span>
                      <span className="font-mono tabular-nums text-[#F5F4F1]">{Math.round(((isClick ? cs.earnings : vs.budget_used) / data.budget_total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-[#0E0D0B] border border-warm rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, ((isClick ? cs.earnings : vs.budget_used) / data.budget_total) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-[#F5F4F1]/45 mt-2 tabular-nums">
                      <span>€{(isClick ? cs.earnings : vs.budget_used || 0).toFixed(2)}</span>
                      <span>€{data.budget_total}</span>
                    </div>
                  </div>
                )}
              </div>
            )

            // ── VIDEOS TAB ──
            : tab === "videos" ? (
              <div className="p-5">
                {(!data.tracked_videos || data.tracked_videos.length === 0) ? (
                  <div className="text-center py-16">
                    <div className="w-10 h-10 rounded-lg bg-[#1C1A17] border border-warm flex items-center justify-center mx-auto mb-3">
                      <Play className="w-4 h-4 text-[#F5F4F1]/30" />
                    </div>
                    <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucune vidéo trackée</p>
                    <p className="text-[#F5F4F1]/35 text-xs mt-1">Les clippers n'ont pas encore publié de vidéo sur cette campagne.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-[#F5F4F1]/55 text-[11px] mb-3 uppercase tracking-widest font-medium">{data.tracked_videos.length} vidéos · triées par vues</p>
                    <div className="space-y-2">
                      {data.tracked_videos.map(v => (
                        <div key={v.video_id || v.url} className="bg-[#1C1A17]/85 border border-warm hover:border-[#F5F4F1]/14 rounded-lg p-3 flex items-center gap-3 transition-all duration-150">
                          {/* Thumbnail */}
                          <div className="w-10 h-14 rounded-md bg-[#0E0D0B] border border-warm overflow-hidden flex-shrink-0">
                            {v.thumbnail_url ? (
                              <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#F5F4F1]/25">
                                <Play className="w-3 h-3" />
                              </div>
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <a href={v.url} target="_blank" rel="noopener noreferrer"
                              className="text-[#F5F4F1] text-xs font-medium line-clamp-1 hover:text-[#00E5FF] transition-colors">
                              {v.title || v.url || "—"}
                            </a>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="text-[#F5F4F1]/45 text-[10px]">{v.clipper_name || "?"}</span>
                              {v.platform && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium border ${
                                  v.platform === "tiktok" ? "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/25" :
                                  v.platform === "instagram" ? "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/25" :
                                  "bg-red-500/10 text-red-400 border-red-500/25"
                                }`}>{v.platform}</span>
                              )}
                              {v.manually_added && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#FFB300]/10 text-[#FFB300] border border-[#FFB300]/25 font-medium">manuel</span>
                              )}
                            </div>
                          </div>
                          {/* Stats */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-[#00E5FF] font-mono text-xs font-semibold tabular-nums">{fmtNum(v.views || 0)}</p>
                            <p className="text-[#39FF14] font-mono text-[10px] tabular-nums">€{(v.earnings || 0).toFixed(2)}</p>
                            <p className="text-[#F5F4F1]/35 text-[10px] tabular-nums">{fmtNum(v.likes || 0)} likes</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )

            // ── CHAT TAB ──
            : tab === "chat" ? (
              <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {data.messages?.length === 0 && (
                    <div className="text-center py-16">
                      <div className="w-10 h-10 rounded-lg bg-[#1C1A17] border border-warm flex items-center justify-center mx-auto mb-3">
                        <MessageCircle className="w-4 h-4 text-[#F5F4F1]/30" />
                      </div>
                      <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun message</p>
                      <p className="text-[#F5F4F1]/35 text-xs mt-1">Le chat de cette campagne est vide.</p>
                    </div>
                  )}
                  {data.messages?.map(msg => (
                    <div key={msg.message_id} className={`flex ${msg.sender_id === "admin" || msg.is_admin ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed border ${
                        msg.is_admin ? "bg-[#FF007F] text-white rounded-br-sm border-[#FF007F]" :
                        msg.sender_role_resolved === "agency" || msg.sender_role === "agency" ? "bg-[#FF007F]/10 text-[#F5F4F1] rounded-bl-sm border-[#FF007F]/25" :
                        "bg-[#1C1A17] text-[#F5F4F1] rounded-bl-sm border-warm"
                      }`}>
                        <p className={`text-[10px] font-display font-medium mb-1 ${msg.is_admin ? "text-white/70" : roleColors[msg.sender_role_resolved || msg.sender_role] || "text-[#F5F4F1]/55"}`}>
                          {msg.is_admin ? "Admin" : msg.sender_display_name || msg.sender_name || "?"}
                        </p>
                        <p>{msg.content}</p>
                        <p className={`text-[9px] mt-1 tabular-nums ${msg.is_admin ? "text-white/55" : "text-[#F5F4F1]/35"}`}>
                          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                {/* Admin send */}
                <div className="p-4 border-t border-warm bg-[#0A0907] flex gap-2 flex-shrink-0">
                  <input
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMsg(); } }}
                    placeholder="Envoyer un message admin dans le chat…"
                    className="flex-1 bg-[#1C1A17] border border-warm rounded-lg px-4 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/25 focus:outline-none focus:border-[#F5F4F1]/22"
                  />
                  <button
                    onClick={handleSendMsg}
                    disabled={sending || !newMsg.trim()}
                    title="Envoyer (Entrée)"
                    className="px-4 py-2.5 bg-[#FF007F] hover:bg-[#E50073] disabled:opacity-40 text-white rounded-lg transition-all duration-150"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )

            // ── MEMBRES TAB ──
            : (
              <div className="p-5">
                {data.members?.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-10 h-10 rounded-lg bg-[#1C1A17] border border-warm flex items-center justify-center mx-auto mb-3">
                      <Users className="w-4 h-4 text-[#F5F4F1]/30" />
                    </div>
                    <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun membre</p>
                    <p className="text-[#F5F4F1]/35 text-xs mt-1">Personne n'a rejoint cette campagne.</p>
                  </div>
                )}
                <div className="space-y-2">
                  {data.members?.map(m => (
                    <div key={m.member_id} className="bg-[#1C1A17]/85 border border-warm hover:border-[#F5F4F1]/14 rounded-lg p-3 flex items-center gap-3 transition-all duration-150">
                      <div className="w-8 h-8 rounded-full bg-[#0E0D0B] border border-warm overflow-hidden flex-shrink-0">
                        {m.user_info?.picture ? (
                          <img src={m.user_info.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-display font-semibold text-[#F5F4F1]/55">
                            {(m.user_info?.display_name || m.user_info?.name || "?")[0]?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F4F1] text-sm font-medium truncate">
                          {m.user_info?.display_name || m.user_info?.name || m.user_id}
                        </p>
                        <p className="text-[#F5F4F1]/45 text-xs truncate">{m.user_info?.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[11px] font-medium ${roleColors[m.role] || "text-[#F5F4F1]/55"}`}>{m.role}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-md border font-medium ${
                          m.status === "active" ? "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25" :
                          m.status === "pending" ? "bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/25" :
                          "bg-[#0E0D0B] text-[#F5F4F1]/55 border-warm"
                        }`}>{m.status}</span>
                        {m.strikes > 0 && (
                          <span className="text-[10px] bg-red-500/15 text-red-400 border border-red-500/25 px-1.5 py-0.5 rounded-md font-mono tabular-nums">
                            {m.strikes} strike{m.strikes > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AdminCampaignsTab ────────────────────────────────────────────────────────

function AdminCampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null); // campaign_id of open detail panel
  const [scrapingId, setScrapingId] = useState(null); // campaign_id en cours de scrape

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    adminFetch("/admin/all-campaigns")
      .then(setCampaigns)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleDelete = async (campaignId) => {
    try {
      await adminFetch(`/admin/campaigns/${campaignId}`, { method: "DELETE" });
      toast.success("Campagne supprimée");
      setConfirmDelete(null);
      if (detailId === campaignId) setDetailId(null);
      fetchCampaigns();
    } catch (e) { toast.error(e.message); }
  };

  const handleForceScrape = async (campaignId, campaignName) => {
    if (scrapingId) return; // déjà en cours
    setScrapingId(campaignId);
    toast.info(`Scraping en cours pour "${campaignName}" — peut prendre 30s à 2min...`);
    try {
      const res = await adminFetch(`/admin/campaigns/${campaignId}/force-scrape`, { method: "POST" });
      toast.success(`✓ Scraping terminé : ${res.total_videos_inserted || 0} vidéos · ${res.total_errors || 0} erreurs · ${res.accounts_scraped || 0} comptes`, { duration: 6000 });
    } catch (e) {
      toast.error(`Erreur scraping : ${e.message}`);
    } finally {
      setScrapingId(null);
    }
  };

  const statusColors = {
    active: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",
    paused: "bg-[#FFB300]/10 text-[#FFB300] border-[#FFB300]/25",
    ended: "bg-red-500/10 text-red-400 border-red-500/25",
    draft: "bg-[#0E0D0B] text-[#F5F4F1]/55 border-warm",
  };

  const filtered = campaigns.filter(c =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.agency_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
            <Play className="w-4.5 h-4.5 text-[#FF007F]" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Campagnes</h2>
          <span className="text-[#F5F4F1]/40 text-sm tabular-nums ml-1">({filtered.length}/{campaigns.length})</span>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Toutes les campagnes — clique sur une ligne pour ouvrir le détail.</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher nom de campagne, agence…"
          className="bg-[#1C1A17] border border-warm rounded-lg px-3 py-2 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/30 focus:outline-none focus:border-[#F5F4F1]/22 flex-1 max-w-md"
        />
        <button onClick={fetchCampaigns} title="Recharger" className="p-2 bg-[#1C1A17] hover:bg-[#262320] border border-warm rounded-lg text-[#F5F4F1]/70 hover:text-[#F5F4F1] transition-all duration-150">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1C1A17]/85 border border-warm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm text-[#F5F4F1]/55 text-[11px] uppercase tracking-wider">
                  <th className="text-left py-3 px-4 font-medium">Campagne</th>
                  <th className="text-left py-3 px-4 font-medium">Agence</th>
                  <th className="text-left py-3 px-4 font-medium">Type</th>
                  <th className="text-left py-3 px-4 font-medium">Statut</th>
                  <th className="text-left py-3 px-4 font-medium">Membres</th>
                  <th className="text-left py-3 px-4 font-medium">Tarif</th>
                  <th className="text-left py-3 px-4 font-medium">Créée le</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.campaign_id} className={`border-b border-warm last:border-b-0 transition-colors cursor-pointer ${detailId === c.campaign_id ? "bg-[#FF007F]/5" : ""}`}
                    onClick={() => setDetailId(c.campaign_id === detailId ? null : c.campaign_id)}>
                    <td className="py-3 px-4 text-[#F5F4F1] font-medium">{c.name}</td>
                    <td className="py-3 px-4 text-[#F5F4F1]/65 text-xs">{c.agency_name}</td>
                    <td className="py-3 px-4">
                      {c.payment_model === "clicks" ? (
                        <span className="inline-flex items-center gap-1 text-[#FFB300] text-[11px] font-medium bg-[#FFB300]/10 border border-[#FFB300]/25 px-2 py-0.5 rounded-md">
                          <MousePointerClick className="w-3 h-3" /> Clic
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#00E5FF] text-[11px] font-medium bg-[#00E5FF]/10 border border-[#00E5FF]/25 px-2 py-0.5 rounded-md">
                          <Eye className="w-3 h-3" /> Vue
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${statusColors[c.status] || statusColors.draft}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#F5F4F1]/70 text-xs tabular-nums">{c.member_count || 0}</td>
                    <td className="py-3 px-4 text-[#39FF14] font-mono text-xs tabular-nums">
                      {c.payment_model === "clicks" ? `€${c.rate_per_click || 0}/clic` : `€${c.rpm || 0}/1K`}
                    </td>
                    <td className="py-3 px-4 text-[#F5F4F1]/45 text-xs tabular-nums">{formatDate(c.created_at)}</td>
                    <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1.5 justify-end">
                        <button
                          onClick={async () => {
                            if (!c.agency_id) { toast.error("Pas d'agence assignée"); return; }
                            try {
                              const res = await fetch(`${API}/admin/preview-as/${c.agency_id}`, {
                                method: "POST", credentials: "include",
                                headers: { "X-Admin-Code": localStorage.getItem(ADMIN_CODE_KEY) || "" },
                              });
                              if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Erreur"); return; }
                              const d = await res.json();
                              window.open(`/agency/campaign/${c.campaign_id}`, "_blank");
                              toast.success(`Connecté comme ${d.user.display_name || d.user.email} (2h) - relog admin requis après`);
                            } catch (e) { toast.error(e.message); }
                          }}
                          className="p-1.5 rounded-md bg-[#FF007F]/10 hover:bg-[#FF007F]/20 text-[#FF007F] border border-[#FF007F]/25 transition-all duration-150"
                          title="Voir comme l'agence (impersonate 2 h)"
                        >
                          <Building2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleForceScrape(c.campaign_id, c.name)}
                          disabled={scrapingId === c.campaign_id}
                          className={`p-1.5 rounded-md transition-all duration-150 border ${scrapingId === c.campaign_id ? "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 cursor-wait" : "bg-[#39FF14]/10 hover:bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/25"}`}
                          title={scrapingId === c.campaign_id ? "Scraping en cours…" : "Forcer un scraping de cette campagne"}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${scrapingId === c.campaign_id ? "animate-spin" : ""}`} />
                        </button>
                        <button
                          onClick={() => setDetailId(c.campaign_id === detailId ? null : c.campaign_id)}
                          className={`p-1.5 rounded-md transition-all duration-150 border ${detailId === c.campaign_id ? "bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/30" : "bg-[#0E0D0B] hover:bg-[#262320] text-[#F5F4F1]/55 hover:text-[#F5F4F1] border-warm"}`}
                          title="Voir détail (panneau latéral)"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(c)}
                          className="p-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 transition-all duration-150"
                          title="Supprimer cette campagne"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-lg bg-[#0E0D0B] border border-warm flex items-center justify-center mb-3">
                          <Play className="w-4 h-4 text-[#F5F4F1]/30" />
                        </div>
                        <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucune campagne</p>
                        <p className="text-[#F5F4F1]/35 text-xs mt-1">Aucun résultat avec la recherche actuelle.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer cette campagne ?"
          message={`Supprimer la campagne "${confirmDelete.name}" et toutes ses données ?`}
          confirmLabel="Supprimer définitivement"
          danger
          onConfirm={() => handleDelete(confirmDelete.campaign_id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {/* Campaign detail slide-over panel */}
      {detailId && (
        <AdminCampaignDetailPanel
          campaignId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}

// ─── SettingsTab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const [confirmAction, setConfirmAction] = useState(null);
  const [inputValue, setInputValue] = useState("");

  const actions = [
    {
      id: "all-campaigns",
      label: "Supprimer toutes les campagnes",
      desc: "Supprime toutes les campagnes, membres et vidéos trackées.",
      confirmWord: "SUPPRIMER",
      endpoint: "/admin/data/all-campaigns",
      method: "DELETE",
      danger: true,
    },
    {
      id: "all-videos",
      label: "Supprimer toutes les vidéos",
      desc: "Supprime uniquement les vidéos trackées (campaigns préservées).",
      confirmWord: "SUPPRIMER",
      endpoint: "/admin/data/all-videos",
      method: "DELETE",
      danger: true,
    },
    {
      id: "all-users",
      label: "Supprimer tous les comptes",
      desc: "⚠️ Supprime tous les comptes utilisateurs (sauf démos). Action irréversible.",
      confirmWord: "TOUT SUPPRIMER",
      endpoint: "/admin/data/all-users",
      method: "DELETE",
      danger: true,
      extreme: true,
    },
  ];

  const executeAction = async (action) => {
    if (inputValue !== action.confirmWord) {
      toast.error(`Tapez exactement : ${action.confirmWord}`);
      return;
    }
    try {
      await adminFetch(action.endpoint, { method: action.method });
      toast.success("Action effectuée");
      setConfirmAction(null);
      setInputValue("");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-7">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
            <Settings className="w-4.5 h-4.5 text-red-400" />
          </div>
          <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight text-[#F5F4F1]">Paramètres</h2>
        </div>
        <p className="text-[#F5F4F1]/55 text-sm">Actions d'administration — certaines sont irréversibles.</p>
      </div>

      <div className="space-y-3 max-w-2xl">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
          <p className="text-[11px] uppercase tracking-widest text-red-400 font-display font-semibold">Zone dangereuse</p>
        </div>
        {actions.map((action) => (
          <div
            key={action.id}
            className={`border rounded-xl p-5 transition-all duration-150 ${action.extreme ? "border-red-500/40 bg-red-500/5" : "border-warm bg-[#1C1A17]/85 hover:border-[#F5F4F1]/14"}`}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-[#F5F4F1] font-display font-semibold text-sm tracking-tight">{action.label}</p>
                <p className="text-[#F5F4F1]/55 text-xs mt-1 leading-relaxed">{action.desc}</p>
              </div>
              <button
                onClick={() => { setConfirmAction(action); setInputValue(""); }}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm rounded-lg transition-all duration-150 whitespace-nowrap font-medium"
              >
                Exécuter
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1C1A17] border border-warm rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4.5 h-4.5 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[#F5F4F1] font-display font-semibold tracking-tight text-base">{confirmAction.label}</h3>
                <p className="text-[#F5F4F1]/60 text-sm mt-1.5 leading-relaxed">{confirmAction.desc}</p>
              </div>
            </div>
            <p className="text-sm text-[#F5F4F1]/65 mb-2">
              Tape <strong className="text-red-400 font-mono">{confirmAction.confirmWord}</strong> pour confirmer :
            </p>
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full bg-[#0E0D0B] border border-warm rounded-lg px-4 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/25 focus:outline-none focus:border-red-500/50 mb-4 font-mono"
              placeholder={confirmAction.confirmWord}
              autoFocus
            />
            <div className="flex gap-2.5">
              <button
                onClick={() => { setConfirmAction(null); setInputValue(""); }}
                className="flex-1 bg-[#0E0D0B] hover:bg-[#262320] border border-warm text-[#F5F4F1] font-medium py-2.5 rounded-lg text-sm transition-all duration-150"
              >
                Annuler
              </button>
              <button
                onClick={() => executeAction(confirmAction)}
                disabled={inputValue !== confirmAction.confirmWord}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg text-sm transition-all duration-150"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SupportTab ───────────────────────────────────────────────────────────────

function SupportTab() {
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(() => {
    setLoadingConvs(true);
    adminFetch("/admin/support/conversations")
      .then((d) => setConversations(d.conversations || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoadingConvs(false));
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const openConversation = useCallback(async (userId) => {
    setSelectedUserId(userId);
    setLoadingMsgs(true);
    try {
      const d = await adminFetch(`/admin/support/messages/${userId}`);
      setMessages(d.messages || []);
      // Refresh convs to update unread counts
      fetchConversations();
    } catch (e) { toast.error(e.message); }
    finally { setLoadingMsgs(false); }
  }, [fetchConversations]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedUserId) return;
    setSending(true);
    try {
      const msg = await adminFetch(`/admin/support/send/${selectedUserId}`, {
        method: "POST",
        body: JSON.stringify({ content: newMessage.trim() }),
      });
      setMessages((prev) => [...prev, msg]);
      setNewMessage("");
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleMsgChange = (e) => setNewMessage(e.target.value);

  const selectedConv = conversations.find((c) => c.user_id === selectedUserId);

  return (
    <div className="flex gap-0 h-[calc(100vh-130px)] min-h-[500px] bg-[#1C1A17]/85 border border-warm rounded-xl overflow-hidden">
      {/* Left: conversations list */}
      <div className="w-72 flex-shrink-0 border-r border-warm flex flex-col bg-[#0E0D0B]">
        <div className="p-4 border-b border-warm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
              <MessageCircle className="w-3.5 h-3.5 text-[#FF007F]" />
            </div>
            <h2 className="text-sm font-display font-semibold tracking-tight text-[#F5F4F1]">Support</h2>
          </div>
          <button onClick={fetchConversations} title="Rafraîchir" className="p-1.5 rounded-md hover:bg-[#1C1A17] text-[#F5F4F1]/55 hover:text-[#F5F4F1] transition-all duration-150">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingConvs ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-10 h-10 rounded-lg bg-[#1C1A17] border border-warm flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-4 h-4 text-[#F5F4F1]/30" />
              </div>
              <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucune conversation</p>
              <p className="text-[#F5F4F1]/35 text-xs mt-1">Les messages des utilisateurs apparaîtront ici.</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <button
                key={conv.user_id}
                onClick={() => openConversation(conv.user_id)}
                className={`w-full text-left px-4 py-3.5 border-b border-warm hover:bg-[#1C1A17] transition-all duration-150 ${selectedUserId === conv.user_id ? "bg-[#FF007F]/10 border-l-2 border-l-[#FF007F]" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/25 flex items-center justify-center text-xs font-display font-semibold text-[#00E5FF] flex-shrink-0">
                    {(conv.user_info?.display_name || conv.user_name || "?")[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F4F1] text-xs font-medium truncate">
                      {conv.user_info?.display_name || conv.user_name || conv.user_id}
                    </p>
                    <p className="text-[#F5F4F1]/40 text-[10px]">{conv.user_role}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="bg-[#FF007F] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 tabular-nums">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-[#F5F4F1]/45 text-[11px] truncate pl-9">
                  {conv.last_from_admin ? "Vous : " : ""}{conv.last_message}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedUserId ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-[#0E0D0B] border border-warm flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-5 h-5 text-[#F5F4F1]/30" />
              </div>
              <p className="text-[#F5F4F1]/55 text-sm font-medium">Sélectionne une conversation</p>
              <p className="text-[#F5F4F1]/35 text-xs mt-1">Choisis un utilisateur dans la liste pour démarrer.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-3.5 border-b border-warm flex items-center gap-3 flex-shrink-0 bg-[#0E0D0B]">
              <div className="w-8 h-8 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/25 flex items-center justify-center text-xs font-display font-semibold text-[#00E5FF]">
                {(selectedConv?.user_info?.display_name || "?")[0]?.toUpperCase()}
              </div>
              <div>
                <p className="text-[#F5F4F1] text-sm font-display font-semibold tracking-tight">
                  {selectedConv?.user_info?.display_name || selectedConv?.user_name || selectedUserId}
                </p>
                <p className="text-[#F5F4F1]/45 text-xs">{selectedConv?.user_info?.email || selectedConv?.user_role}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0E0D0B]">
              {loadingMsgs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-[#F5F4F1]/20 border-t-[#FF007F] rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-10 h-10 rounded-lg bg-[#1C1A17] border border-warm flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-4 h-4 text-[#F5F4F1]/30" />
                  </div>
                  <p className="text-[#F5F4F1]/55 text-sm font-medium">Aucun message</p>
                  <p className="text-[#F5F4F1]/35 text-xs mt-1">Démarre la conversation ci-dessous.</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.message_id} className={`flex ${msg.from_admin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed border ${
                      msg.from_admin
                        ? "bg-[#FF007F] text-white rounded-br-sm border-[#FF007F]"
                        : "bg-[#1C1A17] text-[#F5F4F1] rounded-bl-sm border-warm"
                    }`}>
                      <p>{msg.content}</p>
                      <p className={`text-[10px] mt-1 tabular-nums ${msg.from_admin ? "text-white/55" : "text-[#F5F4F1]/35"}`}>
                        {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-warm flex gap-2 flex-shrink-0 bg-[#0E0D0B]">
              <input
                value={newMessage}
                onChange={handleMsgChange}
                onKeyDown={handleKeyDown}
                placeholder="Répondre au support…"
                className="flex-1 bg-[#1C1A17] border border-warm rounded-lg px-4 py-2.5 text-[#F5F4F1] text-sm placeholder-[#F5F4F1]/25 focus:outline-none focus:border-[#F5F4F1]/22"
              />
              <button
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                title="Envoyer (Entrée)"
                className="px-4 py-2.5 bg-[#FF007F] hover:bg-[#E50073] disabled:opacity-40 text-white rounded-lg transition-all duration-150"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1C1A17] border border-warm rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          {danger && (
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4.5 h-4.5 text-red-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-[#F5F4F1] font-display font-semibold tracking-tight text-base">{title}</h3>
            <p className="text-[#F5F4F1]/60 text-sm mt-1.5 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={onCancel}
            className="flex-1 bg-[#0E0D0B] hover:bg-[#262320] text-[#F5F4F1] font-medium py-2.5 rounded-lg text-sm border border-warm transition-all duration-150"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 ${danger ? "bg-red-500 hover:bg-red-600 text-white" : "bg-[#F5F4F1] hover:bg-white text-[#0E0D0B]"} font-semibold py-2.5 rounded-lg text-sm transition-all duration-150`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function AdminSidebar({ active, setActive, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = [
    { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
    { id: "users", label: "Utilisateurs", icon: Users },
    { id: "campaigns", label: "Campagnes", icon: Play },
    { id: "posts", label: "Tous les posts", icon: Eye },
    { id: "preview-clipper", label: "Preview Clippeur", icon: TrendingUp, preview: true },
    { id: "preview-agency", label: "Preview Agence", icon: Building2, preview: true },
    { id: "preview-manager", label: "Preview Manager", icon: Briefcase, preview: true },
    { id: "preview-client", label: "Preview Client", icon: UserCircle, preview: true },
    { id: "api-status", label: "Connexions API", icon: Plug },
    { id: "usage-monitor", label: "Capacité & Coûts", icon: TrendingUp },
    { id: "scraping-history", label: "Historique Scraping", icon: RefreshCw },
    { id: "fraud-alerts", label: "🚨 Alertes Fraude", icon: AlertTriangle },
    { id: "prospects", label: "Prospects", icon: Building2 },
    { id: "support", label: "Support Chat", icon: MessageCircle },
    { id: "settings", label: "Paramètres", icon: Settings },
  ];

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleSelect = (id) => {
    setActive(id);
    setMobileOpen(false);
  };

  const sidebarBody = (
    <>
      <div className="px-5 py-4 border-b border-warm flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center">
          <Shield className="w-4 h-4 text-[#FF007F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-display font-semibold tracking-tight text-[#F5F4F1] truncate">Admin Panel</p>
          <p className="text-[10px] text-[#F5F4F1]/40 uppercase tracking-widest">The Clip Deal</p>
        </div>
        <button onClick={() => setMobileOpen(false)} className="md:hidden p-1.5 rounded-lg hover:bg-[#1C1A17] text-[#F5F4F1]/55" aria-label="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {items.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleSelect(item.id)}
              title={item.label}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 ${
                isActive
                  ? "bg-[#FF007F]/12 text-[#F5F4F1] border border-[#FF007F]/30"
                  : "text-[#F5F4F1]/55 hover:text-[#F5F4F1] hover:bg-[#1C1A17] border border-transparent"
              } ${item.preview && !isActive ? "opacity-90" : ""}`}
            >
              <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-[#FF007F]" : ""}`} />
              <span className="truncate">{item.label}</span>
              {item.preview && <ExternalLink className="w-3 h-3 ml-auto opacity-40" />}
            </button>
          );
        })}
      </nav>

      <div className="p-3 border-t border-warm">
        <button
          onClick={onLogout}
          title="Se déconnecter de l'admin"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-[#F5F4F1]/55 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion admin
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Hamburger mobile */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 w-10 h-10 rounded-xl bg-[#1C1A17]/95 backdrop-blur border border-warm flex items-center justify-center text-[#F5F4F1] shadow-lg"
        aria-label="Ouvrir le menu admin"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 min-h-screen bg-[#0A0907] border-r border-warm flex-col flex-shrink-0">
        {sidebarBody}
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="md:hidden fixed left-0 top-0 bottom-0 w-[85vw] max-w-[300px] bg-[#0A0907] border-r border-warm flex flex-col z-[60] shadow-2xl"
            >
              {sidebarBody}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── AdminDashboard (main) ────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [unlocked, setUnlocked] = useState(false);
  const [active, setActive] = useState("overview");

  // Check if already unlocked (code in localStorage)
  useEffect(() => {
    const code = localStorage.getItem(ADMIN_CODE_KEY);
    if (code) {
      fetch(`${API}/admin/verify`, {
        headers: { "X-Admin-Code": code },
        credentials: "include",
      }).then((r) => {
        if (r.ok) setUnlocked(true);
        else localStorage.removeItem(ADMIN_CODE_KEY);
      }).catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_CODE_KEY);
    setUnlocked(false);
  };

  if (!unlocked) {
    return <CodeGate onUnlock={() => setUnlocked(true)} />;
  }

  const previewRoles = {
    "preview-clipper": { role: "clipper", label: "Clippeur", icon: Play, color: "bg-blue-500/20" },
    "preview-agency": { role: "agency", label: "Agence", icon: Building2, color: "bg-purple-500/20" },
    "preview-manager": { role: "manager", label: "Manager", icon: Briefcase, color: "bg-amber-500/20" },
    "preview-client": { role: "client", label: "Client", icon: UserCircle, color: "bg-green-500/20" },
  };

  const renderContent = () => {
    if (active === "overview") return <OverviewTab />;
    if (active === "users") return <UsersTab />;
    if (active === "posts") return <PostsTab />;
    if (active === "campaigns") return <AdminCampaignsTab />;
    if (active === "api-status") return <ApiStatusTab />;
    if (active === "usage-monitor") return <UsageMonitorTab />;
    if (active === "scraping-history") return <ScrapingHistoryTab />;
    if (active === "fraud-alerts") return <FraudAlertsTab />;
    if (active === "prospects") return <ProspectsTab />;
    if (active === "settings") return <SettingsTab />;
    if (active === "support") return <SupportTab />;
    if (previewRoles[active]) {
      const p = previewRoles[active];
      return <PreviewTab role={p.role} label={p.label} icon={p.icon} color={p.color} />;
    }
    return null;
  };

  return (
    <div
      className="min-h-screen bg-[#0E0D0B] text-[#F5F4F1] flex select-none"
      onContextMenu={e => e.preventDefault()}
    >
      <AdminSidebar active={active} setActive={setActive} onLogout={handleLogout} />
      <main className="flex-1 p-4 pt-16 md:p-8 overflow-auto relative">
        <div className="bg-warm-radial absolute inset-0 pointer-events-none" />
        <div className="relative">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
